import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent as SubTabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { getIstanbulDateKey } from "@/utils/dateUtils";
import { CheckCircle2, XCircle, Clock, Camera, Settings, MapPin, Loader2, Sun, Cloud, Moon, RefreshCw, AlertCircle, Search, Filter, Video, Image, ChevronRight, TrendingUp, BarChart3, Users, Trophy, Map as MapIcon, PlayCircle, X, Move } from 'lucide-react';
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { toast } from "sonner";
import MediaCapture from "@/components/MediaCapture";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import { MapLayerTile, MapLayerToggle, useMapLayer } from '@/components/map/MapLayerToggle';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';

// Sürüklenebilir Modal Bileşeni
const DraggableModal = ({ isOpen, onClose, title, subtitle, children }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Modal açıldığında pozisyonu sıfırla
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      {/* Arka plan overlay */}
      <div 
        className="absolute inset-0 bg-black/40" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        ref={modalRef}
        className="absolute top-1/2 left-1/2 bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col border"
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        {/* Başlık çubuğu - sürüklemek için */}
        <div 
          data-testid="draggable-modal-header"
          className="flex items-center justify-between p-4 border-b bg-gray-100 rounded-t-lg cursor-grab select-none"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div className="flex items-center gap-3">
            <Move className="h-5 w-5 text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold">{title}</h2>
              {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="no-drag p-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Kapat"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>
        
        {/* Modal İçeriği */}
        <div className="p-4 overflow-y-auto flex-1 bg-white rounded-b-lg">
          {children}
        </div>
      </div>
    </div>
  );
};

// Harita için özel marker ikonları
const createMaintenanceIcon = (status) => {
  const colors = {
    completed_today: '#22c55e', // Yeşil - Bugün bakım yapıldı
    recent: '#eab308', // Sarı - Son 3 günde bakım yapıldı
    pending: '#ef4444' // Kırmızı - Bakım bekliyor
  };
  
  const color = colors[status] || colors.pending;
  
  return L.divIcon({
    className: 'custom-maintenance-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        </svg>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

// Hava durumu uyarı mesajları
const getWeatherWarning = (taskId, weather) => {
  if (!weather || weather === 'normal') return null;
  
  const warnings = {
    rainy: {
      watering: { icon: '🌧️', message: 'Yağmurlu - Sulama yapmayın', color: 'bg-blue-100 text-blue-800 border-blue-300', blocked: true },
      leaf_cleaning: { icon: '🌧️', message: 'Yağmurlu - Dikkatli olun', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      floor_cleaning: { icon: '🌧️', message: 'Yağmurlu - Zemin kaygan olabilir', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      morning_trash: { icon: '🌧️', message: 'Yağmurlu hava - Dikkatli olun', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      noon_trash: { icon: '🌧️', message: 'Yağmurlu hava - Dikkatli olun', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      evening_trash: { icon: '🌧️', message: 'Yağmurlu hava - Dikkatli olun', color: 'bg-blue-100 text-blue-700 border-blue-300' }
    },
    snowy: {
      watering: { icon: '❄️', message: 'Karlı - Sulama yapmayın', color: 'bg-cyan-100 text-cyan-800 border-cyan-300', blocked: true },
      leaf_cleaning: { icon: '❄️', message: 'Karlı - Kar temizliği yapın', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
      floor_cleaning: { icon: '❄️', message: 'Karlı - Zemin kaygan, dikkat!', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' }
    },
    hot: {
      watering: { icon: '🔥', message: 'Sıcak - Sulama önemli!', color: 'bg-orange-100 text-orange-800 border-orange-300' },
      morning_trash: { icon: '🔥', message: 'Sıcak - Erken saatlerde yapın', color: 'bg-orange-100 text-orange-700 border-orange-300' },
      noon_trash: { icon: '🔥', message: 'Sıcak - Öğle sıcağında dikkat!', color: 'bg-red-100 text-red-800 border-red-300' }
    },
    stormy: {
      morning_trash: { icon: '⛈️', message: 'Fırtına - Dikkatli olun', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      noon_trash: { icon: '⛈️', message: 'Fırtına - Dikkatli olun', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      evening_trash: { icon: '⛈️', message: 'Fırtına - Dikkatli olun', color: 'bg-purple-100 text-purple-800 border-purple-300' },
      watering: { icon: '⛈️', message: 'Fırtına - Sulama yapmayın', color: 'bg-purple-100 text-purple-800 border-purple-300', blocked: true },
      leaf_cleaning: { icon: '⛈️', message: 'Fırtına - Ertelemeyi düşünün', color: 'bg-purple-100 text-purple-800 border-purple-300' }
    },
    windy: {
      leaf_cleaning: { icon: '💨', message: 'Rüzgarlı - Yaprak temizliği zor', color: 'bg-gray-100 text-gray-800 border-gray-300' }
    },
    foggy: {
      morning_trash: { icon: '🌫️', message: 'Sisli - Görüş mesafesi düşük', color: 'bg-gray-100 text-gray-700 border-gray-300' }
    }
  };
  
  return warnings[weather]?.[taskId] || null;
};

// Görev öğesi bileşeni (readOnly: izindeyken sadece görüntüleme; completedByName: vekilin yaptığı görevde kimin tamamladığı)
function TaskItem({ task, parkId, personnelId, date, onComplete, isCompleting, weatherCondition, onMediaCapture, readOnly = false, completedByName }) {
  const isCompleted = task.completed;
  const photoRequired = task.photo_required;
  const mediaType = task.media_type || (photoRequired ? 'photo' : 'none'); // "none", "photo", "video", "both"
  const mediaRequired = mediaType !== 'none';
  const onTime = task.on_time;
  const weatherWarning = getWeatherWarning(task.task_id, weatherCondition);
  const isBlocked = weatherWarning?.blocked && !isCompleted;
  const whoCompleted = completedByName || task.completed_by_name;
  
  // Medya sayısı
  const mediaCount = task.media_urls?.length || (task.photo_url ? 1 : 0);
  
  // Zaman kontrolü
  const timeRange = task.expected_time_range;
  const now = new Date();
  const currentTime = now.toTimeString().slice(0, 5); // "HH:MM"
  
  // Zaman durumu hesapla
  const getTimeStatus = () => {
    if (!timeRange || timeRange.length !== 2) return { status: 'no_restriction', canComplete: true };
    
    const [startTime, endTime] = timeRange;
    
    if (currentTime < startTime) {
      return { 
        status: 'too_early', 
        canComplete: false, 
        message: `⏰ Henüz erken! Başlangıç: ${startTime}`,
        color: 'bg-blue-100 text-blue-800 border-blue-300'
      };
    } else if (currentTime > endTime) {
      return { 
        status: 'late', 
        canComplete: true, // Geç olsa da tamamlanabilir
        message: `⚠️ Süre doldu (${endTime}). Geç olarak işaretlenecek.`,
        color: 'bg-orange-100 text-orange-800 border-orange-300'
      };
    }
    return { status: 'on_time', canComplete: true };
  };
  
  const timeStatus = getTimeStatus();
  const isTimeBlocked = !isCompleted && !timeStatus.canComplete;
  
  // Medya tipi etiketi
  const getMediaTypeLabel = () => {
    switch (mediaType) {
      case 'photo': return '📷 Fotoğraf';
      case 'video': return '🎥 Video';
      case 'both': return '📸 Fotoğraf/Video';
      default: return null;
    }
  };
  
  return (
    <div className={`flex flex-col gap-2 p-3 rounded-lg border transition-all ${
      isCompleted 
        ? 'bg-green-50 border-green-200' 
        : isBlocked || isTimeBlocked
          ? 'bg-red-50 border-red-200'
          : timeStatus.status === 'late'
            ? 'bg-orange-50 border-orange-200'
            : weatherWarning 
              ? 'bg-amber-50 border-amber-200' 
              : 'bg-white border-gray-200 hover:border-gray-300'
    }`}>
      {/* Zaman uyarısı - en üstte */}
      {!isCompleted && timeStatus.status !== 'on_time' && timeStatus.status !== 'no_restriction' && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border ${timeStatus.color}`}>
          <span className="font-medium">{timeStatus.message}</span>
        </div>
      )}
      
      {/* Hava durumu uyarısı */}
      {weatherWarning && !isCompleted && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border ${isBlocked ? 'bg-red-100 text-red-800 border-red-300' : weatherWarning.color}`}>
          <span className="text-lg">{weatherWarning.icon}</span>
          <span className="font-medium">{weatherWarning.message}</span>
          {isBlocked && <span className="ml-auto text-xs font-bold">🚫 ENGEL</span>}
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {readOnly ? (
            <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${isCompleted ? 'bg-green-500 border-green-500 text-white' : 'bg-gray-100 border-gray-300'}`} title="Sadece görüntüleme">
              {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5 text-white" /> : null}
            </div>
          ) : (
          <button
            onClick={() => !(isBlocked || isTimeBlocked) && onComplete(parkId, task.task_id, isCompleted)}
            disabled={isCompleting || isBlocked || isTimeBlocked}
            title={isBlocked ? 'Hava koşulları nedeniyle bu görev yapılamaz' : ''}
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${
              isCompleted 
                ? 'bg-green-500 border-green-500 text-white' 
                : isBlocked
                  ? 'bg-gray-200 border-gray-300 cursor-not-allowed'
                  : 'border-gray-300 hover:border-green-400'
            } ${isCompleting ? 'opacity-50' : ''}`}
          >
            {isCompleting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isCompleted ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : isBlocked ? (
              <XCircle className="h-4 w-4 text-gray-400" />
            ) : null}
          </button>
          )}
          
          <div className="flex-1">
            <p className={`font-medium ${isCompleted ? 'line-through text-gray-500' : ''}`}>
              {task.task_name}
            </p>
            {task.completed_at && (
              <p className="text-xs text-muted-foreground">
                Tamamlandı: {new Date(task.completed_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                {onTime === false && (
                  <span className="ml-2 text-amber-600">⚠️ Geç</span>
                )}
              </p>
            )}
            {isCompleted && whoCompleted && (
              <p className="text-xs text-indigo-600 mt-0.5">
                Tamamlayan: {whoCompleted}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Medya Yükleme Butonu - sadece görüntülemede gizle */}
          {!readOnly && mediaRequired && !isBlocked && (
            <Button
              variant="outline"
              size="sm"
              className={`h-7 text-xs ${mediaCount > 0 ? 'border-green-500 text-green-700' : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'}`}
              onClick={() => onMediaCapture && onMediaCapture({ ...task, media_type: mediaType })}
            >
              <Camera className="h-3 w-3 mr-1" />
              {mediaCount > 0 ? `${mediaCount} Medya` : 'Medya Ekle'}
            </Button>
          )}
          
          {/* Fotoğraf/Video Badge */}
          {mediaCount > 0 && (
            <Badge className="text-xs bg-green-500">
              <Image className="h-3 w-3 mr-1" />
              {mediaCount}
            </Badge>
          )}
          
          {/* Medya Gerekli Uyarısı */}
          {mediaRequired && mediaCount === 0 && !isBlocked && (
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
              {getMediaTypeLabel()} Gerekli
            </Badge>
          )}
          
          {onTime === false && isCompleted && (
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
              <AlertCircle className="h-3 w-3 mr-1" />
              Geç
            </Badge>
          )}
          
          {onTime === true && isCompleted && (
            <Badge className="text-xs bg-green-500">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Zamanında
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DailyMaintenanceTab({
  userRole,
  personnelId,
  personnelName = "",
  parks = [],
  personnel = [],
  regions = [],
  userRegionIds = [], // Müdür Yardımcısı için bağlı bölge ID'leri
  API,
  activeSubstitutions = [],
  isOnLeave = false,
  onLeaveSubstitutions = [], // İzindeyken park bazlı vekil: [{ parks: [{ id, name, substitute_name }] }]
  onRefreshData, // Yeni prop - veriyi yenilemek için callback
  onNavigateToPark // Park kartına gitmek için callback (koordinat eksik parklar için)
}) {
  // Sprint 2 F3: Dinamik yetki + rol flag'leri
  const flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = flagsCtx;

  const [loading, setLoading] = useState(false);
  const [mapLayer, setMapLayer] = useMapLayer("street"); // Harita / Uydu
  const [selectedDate, setSelectedDate] = useState(getIstanbulDateKey());
  const [dailySummary, setDailySummary] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("overview");
  
  // Tarihi Türkçe formatla (örn: 02.01.2026)
  const formatDateTR = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  // İzindeyken her park için vekil adı (Parktaki Görevlerim'de takip için)
  const parkDeputyByParkId = useMemo(() => {
    const map = {};
    (onLeaveSubstitutions || []).forEach((s) => {
      const breakdown = Array.isArray(s?.substitute_breakdown) ? s.substitute_breakdown : [];
      (s.parks || []).forEach((p) => {
        const pid = p?.id || p?.park_id;
        // Çöktürülmüş kayıtta üst-seviye substitute_personnel_name yalnız "ilk dolu
        // vekil"dir. Bu parkın gerçek vekilini per-park kırılımdan (substitute_breakdown)
        // çöz; yoksa park-entry alanlarına, en son üst-seviyeye düş.
        const be = pid ? breakdown.find((b) => (b?.id || b?.park_id) === pid) : null;
        const name = (be?.substitute_personnel_name || p?.substitute_name || p?.substitute_personnel_name || s?.substitute_personnel_name || '').trim();
        if (pid && name) map[pid] = name;
      });
    });
    return map;
  }, [onLeaveSubstitutions]);
  
  // Günlük görev oluşturma state'i
  const [creatingDailyTasks, setCreatingDailyTasks] = useState(false);
  
  // Park ayarları için state'ler
  const [selectedParkForSettings, setSelectedParkForSettings] = useState(null);
  const [parkConfig, setParkConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Park arama ve filtreleme (Ayarlar sekmesi için)
  const [settingsParkSearch, setSettingsParkSearch] = useState("");
  const [settingsRegionFilter, setSettingsRegionFilter] = useState("all");
  
  // Toplu hava durumu ayarı
  const [bulkWeatherMode, setBulkWeatherMode] = useState(false);
  const [bulkWeatherSelection, setBulkWeatherSelection] = useState(null);
  const [bulkWeatherRegions, setBulkWeatherRegions] = useState([]); // Çoklu bölge seçimi için array
  const [applyingBulkWeather, setApplyingBulkWeather] = useState(false);
  
  // Genel Bakış - Park Detayları filtreleme ve sayfalama
  const [overviewSearch, setOverviewSearch] = useState("");
  const [overviewStatusFilter, setOverviewStatusFilter] = useState("all"); // all, completed, in_progress, pending
  const [overviewRegionFilter, setOverviewRegionFilter] = useState("all");
  const [overviewSortBy, setOverviewSortBy] = useState("name"); // name, completion, status
  const [overviewPage, setOverviewPage] = useState(1);
  const overviewPageSize = 20;
  
  // Manuel filtreleme için uygulanan filtreler (Ara butonuna basınca aktif olur)
  const [appliedOverviewSearch, setAppliedOverviewSearch] = useState("");
  const [appliedOverviewStatusFilter, setAppliedOverviewStatusFilter] = useState("all");
  const [appliedOverviewRegionFilter, setAppliedOverviewRegionFilter] = useState("all");
  
  // Aktif hava durumu state'i (Toplu uygulama sonrası kalıcı olması için)
  const [activeWeatherStatus, setActiveWeatherStatus] = useState(null);
  
  // Harita sekmesi için state'ler
  const [tempMapStatusFilter, setTempMapStatusFilter] = useState("all"); // Geçici filtre (Ara butonuna basılmadan)
  const [tempMapRegionFilter, setTempMapRegionFilter] = useState("all"); // Geçici filtre
  const [tempMapParkFilter, setTempMapParkFilter] = useState("all"); // Geçici park filtresi (aranabilir)
  const [mapStatusFilter, setMapStatusFilter] = useState("all"); // all, completed_today, recent, pending
  const [mapRegionFilter, setMapRegionFilter] = useState("all");
  const [mapParkFilter, setMapParkFilter] = useState("all"); // tek park seçimi (örn. Zafer Parkı)
  const [mapCenter, setMapCenter] = useState([39.925533, 32.866287]); // Ankara merkez
  const [selectedMapPark, setSelectedMapPark] = useState(null);
  
  // Harita filtrelerini uygula
  const applyMapFilters = () => {
    setMapStatusFilter(tempMapStatusFilter);
    setMapRegionFilter(tempMapRegionFilter);
    setMapParkFilter(tempMapParkFilter);
    // Tek park seçildiyse haritayı o parka odakla
    if (tempMapParkFilter !== "all") {
      const p = (parks || []).find(x => x.id === tempMapParkFilter);
      if (p && p.latitude && p.longitude) setMapCenter([Number(p.latitude), Number(p.longitude)]);
    }
  };

  // Harita filtrelerini temizle
  const clearMapFilters = () => {
    setTempMapStatusFilter("all");
    setTempMapRegionFilter("all");
    setTempMapParkFilter("all");
    setMapStatusFilter("all");
    setMapRegionFilter("all");
    setMapParkFilter("all");
  };
  
  // Görevlerim için state'ler
  const [myTasks, setMyTasks] = useState(null);
  const [loadingMyTasks, setLoadingMyTasks] = useState(false);
  const [completingTask, setCompletingTask] = useState(null); // Hangi görev tamamlanıyor
  const [expandedParks, setExpandedParks] = useState({}); // Hangi parklar açık
  const myTasksRequestRef = useRef(null);
  const dailySummaryRequestRef = useRef(null);
  const chartsRequestRef = useRef(null);
  const myTasksLoadingRef = useRef(false);
  
  // Medya yakalama modal state
  const [mediaCaptureOpen, setMediaCaptureOpen] = useState(false);
  const [selectedTaskForMedia, setSelectedTaskForMedia] = useState(null);
  const [selectedParkIdForMedia, setSelectedParkIdForMedia] = useState(null);
  
  // Detay modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalType, setDetailModalType] = useState(null); // "skipped", "late", "in_progress", "pending", "completed", "on_time"
  const [detailModalData, setDetailModalData] = useState(null);
  const [detailModalTitle, setDetailModalTitle] = useState("");
  
  // Grafik verileri
  const [trendData, setTrendData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [topPersonnel, setTopPersonnel] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [trendDays, setTrendDays] = useState(7); // 7 veya 30 gün
  const [visualSearch, setVisualSearch] = useState("");
  const [visualParkFilter, setVisualParkFilter] = useState("all");
  const [visualPersonnelFilter, setVisualPersonnelFilter] = useState("all");
  const [visualParkQuery, setVisualParkQuery] = useState("");
  const [visualPersonnelQuery, setVisualPersonnelQuery] = useState("");
  const [appliedVisualSearch, setAppliedVisualSearch] = useState("");
  const [appliedVisualParkFilter, setAppliedVisualParkFilter] = useState("all");
  const [appliedVisualPersonnelFilter, setAppliedVisualPersonnelFilter] = useState("all");
  const [appliedVisualParkQuery, setAppliedVisualParkQuery] = useState("");
  
  // Park Görev Detayları Dialog
  const [parkTaskDetailOpen, setParkTaskDetailOpen] = useState(false);
  const [parkTaskDetailData, setParkTaskDetailData] = useState(null);
  const [loadingParkTasks, setLoadingParkTasks] = useState(false);
  
  // Harita için bakım durumu hesaplama fonksiyonu
  const getParkMaintenanceStatus = (parkSummary) => {
    if (!parkSummary) return 'pending';
    
    const today = getIstanbulDateKey();
    const lastTaskDate = parkSummary.last_completed_date;
    
    if (!lastTaskDate) return 'pending';
    
    const lastDate = new Date(lastTaskDate);
    const todayDate = new Date(today);
    const diffDays = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'completed_today'; // Bugün bakım yapıldı
    if (diffDays <= 3) return 'recent'; // Son 3 günde bakım yapıldı
    return 'pending'; // 3+ gün bakım yapılmamış
  };
  
  // Flag'ler component scope'unun başında useRoleFlags()'ten geliyor.
  // Burada kompozit/derived flag'leri tanımlıyoruz.
  const canAccessSettings = isMudur || isMudurYardimcisi || isBolgeMuhendisi || isFormen;
  const canEditSettings = isMudur || isMudurYardimcisi; // Bölge Mühendisi ve Formen sadece görebilir
  const isViewOnlySettings = isBolgeMuhendisi || isFormen; // Sadece görüntüleme modu
  // isParkGorevlisi cim_ekibi/gorevli/park_gorevlisi'i kapsıyor (useRoleFlags)
  const canAccessMyTasks = isParkGorevlisi; // "Görevlerim" sekmesi sadece sahada çalışanlar
  const canAccessVisualOverview =
    isMudur ||
    isMudurYardimcisi ||
    isBolgeMuhendisi ||
    isFormen;

  const resolveMediaUrl = (url) => {
    if (!url) return "";
    const raw = String(url).trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = String(API || "").replace(/\/+$/, "");
    if (!base) return raw;
    return `${base}${raw.startsWith("/") ? "" : "/"}${raw}`;
  };

  const isVideoMedia = (url, mediaType) => {
    if (String(mediaType || "").toLowerCase() === "video") return true;
    return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(String(url || ""));
  };

  const normalizeFilterText = (value) => normalizeSearchText(value);

  const visualMediaRows = useMemo(() => {
    const completions = Array.isArray(dailySummary?.task_completions) ? dailySummary.task_completions : [];
    const rows = [];
    completions.forEach((completion, completionIdx) => {
      const tasks = Array.isArray(completion?.tasks) ? completion.tasks : [];
      tasks.forEach((task, taskIdx) => {
        const mediaUrls = Array.isArray(task?.media_urls) && task.media_urls.length > 0
          ? task.media_urls
          : (task?.photo_url ? [task.photo_url] : []);
        if (!task?.completed || mediaUrls.length === 0) return;
        const normalizedUrls = mediaUrls.map(resolveMediaUrl).filter(Boolean);
        if (normalizedUrls.length === 0) return;
        rows.push({
          id: `${completion?.id || completionIdx}_${task?.task_id || taskIdx}_${taskIdx}`,
          park_id: completion?.park_id,
          park_name: completion?.park_name || "Park",
          personnel_name: task?.completed_by_name || completion?.personnel_name || "-",
          task_name: task?.task_name || task?.task_id || "Görev",
          completed_at: task?.completed_at || completion?.updated_at || completion?.date || null,
          on_time: task?.on_time,
          media_type: task?.media_type || (task?.photo_required ? "photo" : "none"),
          media_urls: normalizedUrls,
        });
      });
    });
    rows.sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
    return rows;
  }, [dailySummary, API]);

  const visualParkOptions = useMemo(() => {
    const completions = Array.isArray(dailySummary?.task_completions) ? dailySummary.task_completions : [];
    return [...new Set(completions.map((c) => c?.park_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  }, [dailySummary]);

  const visualPersonnelOptions = useMemo(() => {
    const completions = Array.isArray(dailySummary?.task_completions) ? dailySummary.task_completions : [];
    const names = [];
    completions.forEach((c) => {
      if (c?.personnel_name) names.push(c.personnel_name);
      (c?.tasks || []).forEach((t) => {
        if (t?.completed_by_name) names.push(t.completed_by_name);
      });
    });
    return [...new Set(names.filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  }, [dailySummary]);

  const filteredVisualParkOptions = useMemo(() => {
    const query = normalizeSearchText(visualParkQuery);
    if (!query) return visualParkOptions;
    return visualParkOptions.filter((name) => normalizeSearchText(name).includes(query));
  }, [visualParkOptions, visualParkQuery]);

  const filteredVisualPersonnelOptions = useMemo(() => {
    const query = normalizeSearchText(visualPersonnelQuery);
    if (!query) return visualPersonnelOptions;
    return visualPersonnelOptions.filter((name) => normalizeSearchText(name).includes(query));
  }, [visualPersonnelOptions, visualPersonnelQuery]);

  const visualExampleRows = useMemo(() => ([
    {
      id: "example_1",
      park_name: "DÜNYA SİTESİ PARKI",
      personnel_name: "Reyfet Başer",
      task_name: "Sabah Mıntıkası",
      on_time: true,
      media_type: "photo",
      search_key: "DÜNYA SİTESİ",
    },
    {
      id: "example_2",
      park_name: "CAN YÜCEL PARKI",
      personnel_name: "Ali Şahin",
      task_name: "Havuz Temizliği",
      on_time: false,
      media_type: "video",
      search_key: "CAN YÜCEL",
    }
  ]), []);

  const filteredVisualMediaRows = useMemo(() => {
    const search = normalizeFilterText(appliedVisualSearch || "");
    const selectedPark = normalizeFilterText(appliedVisualParkFilter === "all" ? "" : appliedVisualParkFilter);
    const selectedPersonnel = normalizeFilterText(appliedVisualPersonnelFilter === "all" ? "" : appliedVisualPersonnelFilter);
    const selectedParkQuery = normalizeFilterText(appliedVisualParkQuery || "");
    return visualMediaRows.filter((row) => {
      const rowPark = normalizeFilterText(row.park_name);
      const rowPersonnel = normalizeFilterText(row.personnel_name);
      const rowTask = normalizeFilterText(row.task_name);
      if (selectedPark && rowPark !== selectedPark) return false;
      if (!selectedPark && selectedParkQuery && !rowPark.includes(selectedParkQuery)) return false;
      if (selectedPersonnel && rowPersonnel !== selectedPersonnel) return false;
      if (!search) return true;
      return (
        rowPark.includes(search) ||
        rowPersonnel.includes(search) ||
        rowTask.includes(search)
      );
    });
  }, [
    visualMediaRows,
    appliedVisualSearch,
    appliedVisualParkFilter,
    appliedVisualPersonnelFilter,
    appliedVisualParkQuery
  ]);

  const filteredVisualExampleRows = useMemo(() => {
    const search = normalizeFilterText(appliedVisualSearch || "");
    const selectedPark = normalizeFilterText(appliedVisualParkFilter === "all" ? "" : appliedVisualParkFilter);
    const selectedPersonnel = normalizeFilterText(appliedVisualPersonnelFilter === "all" ? "" : appliedVisualPersonnelFilter);
    const selectedParkQuery = normalizeFilterText(appliedVisualParkQuery || "");
    return visualExampleRows.filter((row) => {
      const rowPark = normalizeFilterText(row.park_name);
      const rowPersonnel = normalizeFilterText(row.personnel_name);
      const rowTask = normalizeFilterText(row.task_name);
      if (selectedPark && rowPark !== selectedPark) return false;
      if (!selectedPark && selectedParkQuery && !rowPark.includes(selectedParkQuery)) return false;
      if (selectedPersonnel && rowPersonnel !== selectedPersonnel) return false;
      if (!search) return true;
      return (
        rowPark.includes(search) ||
        rowPersonnel.includes(search) ||
        rowTask.includes(search)
      );
    });
  }, [
    visualExampleRows,
    appliedVisualSearch,
    appliedVisualParkFilter,
    appliedVisualPersonnelFilter,
    appliedVisualParkQuery
  ]);

  const applyVisualFilters = () => {
    const hasScopedFilter =
      visualParkFilter !== "all" ||
      visualPersonnelFilter !== "all" ||
      String(visualParkQuery || "").trim().length > 0 ||
      String(visualPersonnelQuery || "").trim().length > 0;
    // Scoped filtreler varken genel arama karışmasın.
    setAppliedVisualSearch(hasScopedFilter ? "" : visualSearch);
    setAppliedVisualParkFilter(visualParkFilter);
    setAppliedVisualPersonnelFilter(visualPersonnelFilter);
    // Park dropdown seçimi varsa park query'yi devre dışı bırak (öncelik seçili parkta).
    setAppliedVisualParkQuery(visualParkFilter !== "all" ? "" : visualParkQuery);
  };

  const clearVisualFilters = () => {
    setVisualSearch("");
    setVisualParkFilter("all");
    setVisualPersonnelFilter("all");
    setVisualParkQuery("");
    setVisualPersonnelQuery("");
    setAppliedVisualSearch("");
    setAppliedVisualParkFilter("all");
    setAppliedVisualPersonnelFilter("all");
    setAppliedVisualParkQuery("");
  };

  const applyVisualExample = (searchText, infoText) => {
    setVisualParkFilter("all");
    setVisualPersonnelFilter("all");
    setVisualSearch(searchText || "");
    setAppliedVisualParkFilter("all");
    setAppliedVisualPersonnelFilter("all");
    setAppliedVisualSearch(searchText || "");
    setAppliedVisualParkQuery("");
    if (infoText) {
      toast.info(infoText);
    }
  };
  
  // Rol bazlı filtrelenmiş parklar
  const filteredParksForSettings = React.useMemo(() => {
    if (isMudur || isMudur) {
      return parks; // Müdür tüm parkları görür
    }
    if (isMudurYardimcisi && userRegionIds.length > 0) {
      // Müdür Yardımcısı sadece kendi bölgelerindeki parkları görür
      return parks.filter(park => userRegionIds.includes(park.region_id));
    }
    if (isBolgeMuhendisi) {
      // Bölge Mühendisi - atandığı bölgelerdeki parkları görür
      const assignedRegionIds = regions
        .filter(r => r.assigned_bolge_muhendisi_id === personnelId)
        .map(r => r.id);
      return parks.filter(park => assignedRegionIds.includes(park.region_id));
    }
    if (isFormen) {
      // Formen - atandığı bölgelerdeki parkları görür
      const assignedRegionIds = regions
        .filter(r => r.assigned_formen_ids?.includes(personnelId))
        .map(r => r.id);
      return parks.filter(park => assignedRegionIds.includes(park.region_id));
    }
    if (isParkGorevlisi) {
      // Park Görevlisi - sadece kendine atanmış parkları görür
      // İki yönlü kontrol: personnel.assigned_park_ids VEYA park.assigned_personnel_ids
      const currentPersonnel = personnel.find(p => p.id === personnelId);
      const assignedParkIds = currentPersonnel?.assigned_park_ids || [];
      
      return parks.filter(park => 
        assignedParkIds.includes(park.id) || 
        (park.assigned_personnel_ids || []).includes(personnelId)
      );
    }
    return parks;
  }, [parks, userRole, isMudurYardimcisi, userRegionIds, regions, personnelId, personnel]);
  
  const filteredRegionsForSettings = React.useMemo(() => {
    if (isMudur) {
      return regions; // Müdür tüm bölgeleri görür
    }
    if (isMudurYardimcisi && userRegionIds.length > 0) {
      // Müdür Yardımcısı sadece kendi bölgelerini görür
      return regions.filter(region => userRegionIds.includes(region.id));
    }
    if (isBolgeMuhendisi) {
      // Bölge Mühendisi sadece atandığı bölgeleri görür
      return regions.filter(region => region.assigned_bolge_muhendisi_id === personnelId);
    }
    if (isFormen) {
      // Formen sadece atandığı bölgeleri görür
      return regions.filter(region => region.assigned_formen_ids?.includes(personnelId));
    }
    return regions;
  }, [regions, userRole, isMudurYardimcisi, userRegionIds, personnelId]);

  // Harita için filtrelenmiş parklar
  const filteredParksForMap = React.useMemo(() => {
    let filtered = filteredParksForSettings || [];
    
    // Park filtresi (tek park seçimi — örn. Zafer Parkı)
    if (mapParkFilter !== "all") {
      filtered = filtered.filter(park => park.id === mapParkFilter);
    }

    // Bölge filtresi
    if (mapRegionFilter !== "all") {
      filtered = filtered.filter(park => park.region_id === mapRegionFilter);
    }

    // Bakım durumu filtresi
    if (mapStatusFilter !== "all" && dailySummary?.parks) {
      filtered = filtered.filter(park => {
        const parkSummary = dailySummary.parks.find(p => p.park_id === park.id);
        const status = getParkMaintenanceStatus(parkSummary);
        return status === mapStatusFilter;
      });
    }
    
    // Sadece koordinatı olan parkları göster
    return filtered.filter(park => park.latitude && park.longitude);
  }, [filteredParksForSettings, mapParkFilter, mapRegionFilter, mapStatusFilter, dailySummary]);

  // Koordinatı eksik parklar
  const parksWithoutCoordinates = React.useMemo(() => {
    return (filteredParksForSettings || []).filter(park => !park.latitude || !park.longitude);
  }, [filteredParksForSettings]);

  // Bugünün tarihini al
  const today = getIstanbulDateKey();

  // Sayfa ilk yüklendiğinde veri getir (sadece bir kez)
  useEffect(() => {
    loadDailySummary();
  }, [personnelId]); // selectedDate kaldırıldı - manuel yenileme için

  // Otomatik yenileme (polling) - Genel Bakış ve Görevlerim sekmeleri için
  // Daha seyrek ve görünürken çalıştır (yükü azalt)
  useEffect(() => {
    if (activeSubTab === 'overview' || activeSubTab === 'tasks' || activeSubTab === 'substitutions') {
      const interval = setInterval(() => {
        if (document.visibilityState !== 'visible') return;
        // Silent mode: loading gösterme, sadece veri güncelle
        if (activeSubTab === 'overview') {
          // Genel Bakış sekmesi - dailySummary verisini güncelle
          loadDailySummary(true);
          if (!isParkGorevlisi) {
            loadChartData(true);
          }
        }
        if (activeSubTab === 'tasks' || activeSubTab === 'substitutions') {
          // Görevlerim/Vekalet Parklar sekmesi aktifken myTasks verisini güncelle
          loadMyTasks(true);
        }
      }, 30000); // 30 saniye
      
      return () => clearInterval(interval);
    }
  }, [activeSubTab, selectedDate, personnelId, isParkGorevlisi]);

  // Görevlerim sekmesi için veri yükle (sekme değiştiğinde)
  useEffect(() => {
    if ((activeSubTab === 'tasks' || activeSubTab === 'substitutions') && personnelId) {
      loadMyTasks();
    }
  }, [activeSubTab, personnelId]); // selectedDate kaldırıldı - manuel yenileme için

  // Vekil görev tamamladığında park sahibine SSE ile bildirim gelir; listeyi anında yenile
  useEffect(() => {
    const handler = () => loadMyTasks(true);
    window.addEventListener('parkTasksRefresh', handler);
    return () => window.removeEventListener('parkTasksRefresh', handler);
  }, [personnelId]);

  // Görev tamamlandığında (kendi veya vekil) Park Bakım Yönetimi özeti anında yenilensin
  useEffect(() => {
    const handler = () => loadDailySummary(true);
    window.addEventListener('maintenanceSummaryRefresh', handler);
    return () => window.removeEventListener('maintenanceSummaryRefresh', handler);
  }, []);
  
  // Görevlerim sekmesi için istek iptali (sekmeden çıkarken gereksiz yükü önle)
  useEffect(() => {
    return () => {
      if (myTasksRequestRef.current) {
        myTasksRequestRef.current.abort();
        myTasksRequestRef.current = null;
      }
    };
  }, []);

  // Not: Ayarlar sekmesi için ayrı polling yok - App.js'te global 30 saniyelik polling var

  const loadMyTasks = async (silent = false) => {
    if (!personnelId) return;
    // İzindeyken de yükle: parklar ve vekilin yaptığı görevler görüntülenebilsin (sadece görüntüleme)

    if (myTasksLoadingRef.current) return;
    myTasksLoadingRef.current = true;

    try {
      // Silent modda loading gösterme (otomatik yenileme için)
      if (!silent) {
        setLoadingMyTasks(true);
      }

      if (myTasksRequestRef.current) {
        myTasksRequestRef.current.abort();
      }
      const controller = new AbortController();
      myTasksRequestRef.current = controller;

      const response = await axios.get(
        `${API}/api/daily-tasks/my-tasks/${personnelId}/${selectedDate}`,
        { signal: controller.signal }
      );
      
      if (response.data.success) {
        const normalizedParks = Array.isArray(response.data.parks) ? response.data.parks : [];
        const substitutionParks = Array.isArray(response.data.substitution_parks) ? response.data.substitution_parks : [];
        setMyTasks({ ...response.data, parks: normalizedParks, substitution_parks: substitutionParks });
        // Sadece ilk yüklemede parkları aç, silent modda mevcut state'i koru
        if (!silent) {
          const expanded = {};
          normalizedParks.forEach(park => { expanded[park.park_id] = true; });
          substitutionParks.forEach(park => { expanded[park.park_id] = true; });
          setExpandedParks(expanded);
        }
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Görevler yüklenirken hata:', error);
      if (!silent) {
        toast.error('Görevler yüklenemedi');
      }
    } finally {
      myTasksLoadingRef.current = false;
      if (!silent) {
        setLoadingMyTasks(false);
      }
      myTasksRequestRef.current = null;
    }
  };

  // Park Görev Detaylarını yükle (Genel Bakış tablosundan tıklayınca)
  const loadParkTaskDetails = async (completion) => {
    setLoadingParkTasks(true);
    setParkTaskDetailOpen(true);
    
    try {
      // daily-tasks endpoint'inden görev detaylarını çek
      const response = await axios.get(`${API}/api/daily-tasks/${completion.park_id}/${selectedDate}`);
      
      if (response.data && response.data.task_completion) {
        setParkTaskDetailData({
          ...completion,
          tasks: response.data.task_completion.tasks || [],
          weather_condition: response.data.task_completion.weather_condition,
          completed_tasks: response.data.task_completion.completed_tasks,
          total_tasks: response.data.task_completion.total_tasks,
          completion_rate: response.data.task_completion.completion_rate,
          personnel_id: response.data.task_completion.personnel_id,
          personnel_name: response.data.task_completion.personnel_name
        });
      } else if (response.data && response.data.tasks) {
        // Alternatif yapı
        setParkTaskDetailData({
          ...completion,
          tasks: response.data.tasks || [],
          weather_condition: response.data.weather_condition
        });
      } else {
        setParkTaskDetailData({
          ...completion,
          tasks: [],
          error: false
        });
      }
    } catch (error) {
      console.error('Görev detayları yüklenirken hata:', error);
      // Hata durumunda completion verisini göster
      setParkTaskDetailData({
        ...completion,
        tasks: [],
        error: true
      });
    } finally {
      setLoadingParkTasks(false);
    }
  };

  // Park detay modalı açıksa, ilgili parkın görevlerini arka planda yenile
  const refreshOpenParkTaskDetail = async () => {
    if (!parkTaskDetailOpen || !parkTaskDetailData?.park_id) return;
    try {
      const response = await axios.get(`${API}/api/daily-tasks/${parkTaskDetailData.park_id}/${selectedDate}`);
      if (response.data?.task_completion) {
        setParkTaskDetailData(prev =>
          prev
            ? {
                ...prev,
                tasks: response.data.task_completion.tasks || [],
                weather_condition: response.data.task_completion.weather_condition,
                completed_tasks: response.data.task_completion.completed_tasks,
                total_tasks: response.data.task_completion.total_tasks,
                completion_rate: response.data.task_completion.completion_rate,
                personnel_id: response.data.task_completion.personnel_id,
                personnel_name: response.data.task_completion.personnel_name
              }
            : prev
        );
      } else if (response.data?.tasks) {
        setParkTaskDetailData(prev =>
          prev
            ? {
                ...prev,
                tasks: response.data.tasks || [],
                weather_condition: response.data.weather_condition
              }
            : prev
        );
      }
    } catch (error) {
      console.error('Açık park detayları yenilenirken hata:', error);
    }
  };

  useEffect(() => {
    const handler = () => refreshOpenParkTaskDetail();
    window.addEventListener('parkTasksRefresh', handler);
    window.addEventListener('maintenanceSummaryRefresh', handler);
    return () => {
      window.removeEventListener('parkTasksRefresh', handler);
      window.removeEventListener('maintenanceSummaryRefresh', handler);
    };
  }, [parkTaskDetailOpen, parkTaskDetailData?.park_id, selectedDate]);

  // SSE gelmese bile modal açıkken kısa aralıklarla yenile (canlı hissi)
  useEffect(() => {
    if (!parkTaskDetailOpen || !parkTaskDetailData?.park_id) return;
    const interval = setInterval(() => {
      refreshOpenParkTaskDetail();
    }, 2000);
    return () => clearInterval(interval);
  }, [parkTaskDetailOpen, parkTaskDetailData?.park_id, selectedDate]);

  const openDetailCoverage = useMemo(() => {
    if (!parkTaskDetailData?.park_id || !parkTaskDetailData?.personnel_id) return null;
    const sub = (activeSubstitutions || []).find((sub) =>
      sub?.original_personnel_id === parkTaskDetailData.personnel_id &&
      Array.isArray(sub?.parks) &&
      sub.parks.some((p) => (typeof p === "string" ? p : (p?.id || p?.park_id)) === parkTaskDetailData.park_id)
    ) || null;
    if (!sub) return null;
    // Çöktürülmüş kayıtta üst-seviye substitute_personnel_name yalnız "ilk dolu
    // vekil"dir. Bu parkın gerçek vekilini per-park kırılımdan (substitute_breakdown)
    // çöz; yoksa üst-seviye alanı koru.
    const breakdown = Array.isArray(sub.substitute_breakdown) ? sub.substitute_breakdown : [];
    const be = breakdown.find((b) => (b?.id || b?.park_id) === parkTaskDetailData.park_id);
    if (be?.substitute_personnel_name) {
      return { ...sub, substitute_personnel_name: be.substitute_personnel_name };
    }
    return sub;
  }, [activeSubstitutions, parkTaskDetailData?.park_id, parkTaskDetailData?.personnel_id]);

  const handleTaskComplete = async (parkId, taskId, currentCompleted) => {
    const taskKey = `${parkId}-${taskId}`;
    setCompletingTask(taskKey);
    
    try {
      const response = await axios.post(`${API}/api/daily-tasks/complete-task`, null, {
        params: {
          park_id: parkId,
          date: selectedDate,
          personnel_id: personnelId,
          task_id: taskId,
          completed: !currentCompleted
        }
      });
      
      if (response.data.success) {
        // Listeyi backend'den yenile (vekalet parkı / completed_by dahil güncel veri)
        loadMyTasks(true);
        toast.success(!currentCompleted ? 'Görev tamamlandı ✓' : 'Görev geri alındı');
      }
    } catch (error) {
      console.error('Görev güncellenirken hata:', error);
      
      // Zaman kontrolü hatası
      if (error.response?.status === 400 && error.response?.data?.detail?.error === 'time_restriction') {
        const detail = error.response.data.detail;
        toast.error(detail.message, {
          description: `Şu anki saat: ${detail.current_time} | İzin verilen: ${detail.start_time} - ${detail.end_time}`,
          duration: 5000
        });
      } else if (error.response?.status === 400 && error.response?.data?.detail?.error === 'photo_required') {
        // Medya zorunlu görev: medya eklenmeden tamamlanamaz
        const detail = error.response.data.detail;
        toast.error(detail.message || 'Bu görev için medya (fotoğraf/video) zorunludur.', {
          description: 'Lütfen önce "Medya Ekle" ile fotoğraf/video yükleyin, sonra görevi işaretleyin.',
          duration: 5000
        });
      } else {
        toast.error('Görev güncellenemedi');
      }
    } finally {
      setCompletingTask(null);
    }
  };

  const toggleParkExpand = (parkId) => {
    setExpandedParks(prev => ({
      ...prev,
      [parkId]: !prev[parkId]
    }));
  };

  // Medya yakalama handler
  const handleMediaCapture = (parkId, task) => {
    setSelectedParkIdForMedia(parkId);
    setSelectedTaskForMedia(task);
    setMediaCaptureOpen(true);
  };

  const handleMediaUploadComplete = (uploadedMedia) => {
    // Görev listesini yenile
    loadMyTasks();
    setMediaCaptureOpen(false);
    setSelectedTaskForMedia(null);
    setSelectedParkIdForMedia(null);
  };
  
  // Grafik verilerini yükle
  const loadChartData = async (silent = false) => {
    if (isParkGorevlisi) return; // Park görevlisi grafik görmez
    
    // Silent modda loading gösterme
    if (!silent) {
      setChartsLoading(true);
    }
    try {
      // Rol bazlı parametreler
      let params = {};
      if (isFormen) {
        params.formen_id = personnelId;
      } else if (isBolgeMuhendisi) {
        params.bolge_muhendisi_id = personnelId;
      } else if (isMudurYardimcisi) {
        params.mudur_yardimcisi_id = personnelId;
      }
      
      // Paralel istekler
      if (chartsRequestRef.current) {
        chartsRequestRef.current.abort();
      }
      const controller = new AbortController();
      chartsRequestRef.current = controller;
      const [trendRes, regionRes, personnelRes] = await Promise.all([
        axios.get(`${API}/api/maintenance/charts/trend`, { params: { ...params, days: trendDays }, signal: controller.signal }),
        axios.get(`${API}/api/maintenance/charts/regions`, { params: { date: selectedDate, ...params }, signal: controller.signal }),
        axios.get(`${API}/api/maintenance/charts/top-personnel`, { params: { date: selectedDate, limit: 10, ...params }, signal: controller.signal })
      ]);
      
      if (trendRes.data.success) setTrendData(trendRes.data.trend);
      if (regionRes.data.success) setRegionData(regionRes.data.regions);
      if (personnelRes.data.success) setTopPersonnel(personnelRes.data.personnel);
      
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Grafik verileri yüklenirken hata:', error);
      }
    } finally {
      chartsRequestRef.current = null;
      if (!silent) {
        setChartsLoading(false);
      }
    }
  };
  
  // Grafik verilerini yükle (tarih veya gün sayısı değiştiğinde)
  useEffect(() => {
    if (!isParkGorevlisi && activeSubTab === 'overview') {
      loadChartData();
    }
  }, [selectedDate, trendDays, activeSubTab]);

  // Detay modal açma fonksiyonu
  const openDetailModal = (type, taskId = null, taskName = null) => {
    if (!dailySummary) return;
    
    let data = [];
    let title = "";
    const dateStr = formatDateTR(selectedDate);
    
    switch (type) {
      case "skipped":
        // Belirli görevi atlayan parklar/personeller
        title = `🚫 "${taskName}" Görevini Atlayanlar (${dateStr})`;
        data = dailySummary.task_completions
          .filter(tc => tc.tasks?.some(t => t.task_id === taskId && !t.completed))
          .map(tc => ({
            park_name: tc.park_name,
            personnel_name: tc.personnel_name,
            status: tc.status,
            completion_rate: tc.completion_rate
          }))
          .sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "late":
        // Belirli görevi geç tamamlayanlar (vekil yaptıysa completed_by_name ile gösterilecek)
        title = `⏰ "${taskName}" Görevini Geç Tamamlayanlar (${dateStr})`;
        data = dailySummary.task_completions
          .filter(tc => tc.tasks?.some(t => t.task_id === taskId && t.completed && (t.on_time === false || t.time_status === "late")))
          .map(tc => {
            const task = tc.tasks.find(t => t.task_id === taskId);
            return {
              park_name: tc.park_name,
              personnel_name: tc.personnel_name,
              completed_by_name: task?.completed_by_name,
              completed_at: task?.completed_at,
              expected_time: task?.expected_time_range ? `${task.expected_time_range[0]} - ${task.expected_time_range[1]}` : "-"
            };
          })
          .sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "in_progress":
        // Devam eden parklar (tamamlanan görevlerde vekil varsa completed_by_names)
        title = `🔄 Devam Eden Parklar (${dateStr})`;
        data = dailySummary.task_completions
          .filter(tc => tc.status === "in_progress")
          .map(tc => {
            const byNames = [...new Set((tc.tasks || []).filter(t => t.completed && t.completed_by_name).map(t => t.completed_by_name))];
            return {
              park_name: tc.park_name,
              personnel_name: tc.personnel_name,
              completed_by_names: byNames,
              completion_rate: tc.completion_rate,
              completed_tasks: tc.completed_tasks,
              total_tasks: tc.total_tasks
            };
          })
          .sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "pending":
        // Bekleyen parklar
        title = `⏳ Bekleyen Parklar - Hiç başlanmamış (${dateStr})`;
        data = dailySummary.task_completions
          .filter(tc => tc.status === "pending")
          .map(tc => ({
            park_name: tc.park_name,
            personnel_name: tc.personnel_name,
            total_tasks: tc.total_tasks
          }))
          .sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "completed":
        // Tamamlanan parklar (vekil tamamladıysa completed_by_names)
        title = `✅ Tamamlanan Parklar (${dateStr})`;
        data = dailySummary.task_completions
          .filter(tc => tc.status === "completed")
          .map(tc => {
            const byNames = [...new Set((tc.tasks || []).filter(t => t.completed && t.completed_by_name).map(t => t.completed_by_name))];
            return {
              park_name: tc.park_name,
              personnel_name: tc.personnel_name,
              completed_by_names: byNames,
              on_time_rate: tc.on_time_rate
            };
          })
          .sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "on_time":
        // Zamanında tamamlanan görevler (vekil yaptıysa completed_by_name ile gösterilir)
        title = `✅ Zamanında Tamamlanan Görevler (${dateStr})`;
        data = [];
        dailySummary.task_completions.forEach(tc => {
          tc.tasks?.forEach(task => {
            if (task.completed && task.on_time === true) {
              data.push({
                park_name: tc.park_name,
                personnel_name: tc.personnel_name,
                completed_by_name: task.completed_by_name,
                task_name: task.task_name,
                completed_at: task.completed_at
              });
            }
          });
        });
        data.sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      case "late_all":
        // Tüm geç tamamlanan görevler (vekil yaptıysa completed_by_name ile kimin yaptığı gösterilir)
        title = `⚠️ Geç Tamamlanan Tüm Görevler (${dateStr})`;
        data = [];
        dailySummary.task_completions.forEach(tc => {
          tc.tasks?.forEach(task => {
            if (task.completed && (task.on_time === false || task.time_status === "late")) {
              data.push({
                park_name: tc.park_name,
                personnel_name: tc.personnel_name,
                completed_by_name: task.completed_by_name,
                task_name: task.task_name,
                completed_at: task.completed_at,
                expected_time: task.expected_time_range ? `${task.expected_time_range[0]} - ${task.expected_time_range[1]}` : "-"
              });
            }
          });
        });
        data.sort((a, b) => a.park_name.localeCompare(b.park_name, 'tr'));
        break;
        
      default:
        return;
    }
    
    setDetailModalType(type);
    setDetailModalTitle(title);
    setDetailModalData(data);
    setDetailModalOpen(true);
  };

  const getTimeSlotIcon = (timeSlot) => {
    switch (timeSlot) {
      case 'morning':
        return <Sun className="h-4 w-4 text-yellow-500" />;
      case 'noon':
        return <Cloud className="h-4 w-4 text-orange-500" />;
      case 'evening':
        return <Moon className="h-4 w-4 text-blue-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTimeSlotLabel = (timeSlot) => {
    switch (timeSlot) {
      case 'morning':
        return 'Sabah';
      case 'noon':
        return 'Öğle';
      case 'evening':
        return 'Akşam';
      default:
        return 'Genel';
    }
  };

  const groupTasksByTimeSlot = (tasks) => {
    const grouped = {
      morning: [],
      noon: [],
      evening: [],
      general: []
    };
    
    tasks?.forEach(task => {
      const slot = task.time_slot || 'general';
      if (grouped[slot]) {
        grouped[slot].push(task);
      } else {
        grouped.general.push(task);
      }
    });
    
    return grouped;
  };

  const loadDailySummary = async (silent = false) => {
    try {
      // Silent modda loading gösterme (otomatik yenileme için)
      if (!silent) {
        setLoading(true);
      }
      
      // Rol bazlı parametreler
      let params = { date: selectedDate };
      
      if (isParkGorevlisi) {
        params.personnel_id = personnelId;
      } else if (isFormen) {
        params.formen_id = personnelId;
      } else if (isBolgeMuhendisi) {
        params.bolge_muhendisi_id = personnelId;
      } else if (isMudurYardimcisi) {
        params.mudur_yardimcisi_id = personnelId;
      }
      // Müdür için parametre yok, hepsini görür

      if (dailySummaryRequestRef.current) {
        dailySummaryRequestRef.current.abort();
      }
      const controller = new AbortController();
      dailySummaryRequestRef.current = controller;
      const response = await axios.get(`${API}/api/maintenance/summary/${selectedDate}`, { params, signal: controller.signal });
      
      const data = response?.data;
      if (data && (data.success === true || data.summary)) {
        setDailySummary({
          success: true,
          summary: data.summary ?? { total_parks: 0, total_tasks: 0, completed_tasks: 0, avg_completion_rate: 0, on_time_tasks: 0, late_tasks: 0, status_counts: { completed: 0, in_progress: 0, pending: 0 } },
          detailed_stats: data.detailed_stats ?? { most_skipped_tasks: [], most_late_tasks: [], task_stats: {} },
          task_completions: data.task_completions ?? [],
          parks: data.parks ?? []
        });
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Günlük özet yüklenirken hata:', error);
      }
      if (!silent) {
        const msg = error.response?.data?.detail || error.message || 'Günlük özet yüklenemedi';
        toast.error(typeof msg === 'string' ? msg : 'Günlük özet yüklenemedi');
      }
    } finally {
      dailySummaryRequestRef.current = null;
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const loadParkConfig = async (parkId) => {
    try {
      const response = await axios.get(`${API}/api/parks/${parkId}/daily-tasks-config`);
      if (response.data.success) {
        setParkConfig(response.data.config);
      }
    } catch (error) {
      console.error('Park ayarları yüklenirken hata:', error);
      toast.error('Park ayarları yüklenemedi');
    }
  };

  const saveParkConfig = async () => {
    if (!selectedParkForSettings || !parkConfig) return;
    
    try {
      setSavingConfig(true);
      const response = await axios.put(
        `${API}/api/parks/${selectedParkForSettings}/daily-tasks-config`,
        parkConfig
      );
      
      if (response.data.success) {
        toast.success('Park ayarları kaydedildi');
      }
    } catch (error) {
      console.error('Park ayarları kaydedilirken hata:', error);
      toast.error('Park ayarları kaydedilemedi');
    } finally {
      setSavingConfig(false);
    }
  };

  // Günlük görevleri manuel oluştur
  const createDailyTasks = async () => {
    try {
      setCreatingDailyTasks(true);
      
      const response = await axios.post(`${API}/api/maintenance/create-daily-tasks?date=${selectedDate}`);
      
      if (response.data.success) {
        const { created_count, skipped_count } = response.data;
        
        if (created_count > 0) {
          toast.success(`${created_count} park için görev kaydı oluşturuldu`);
          // Verileri yenile
          loadDailySummary();
          if (isParkGorevlisi) {
            loadMyTasks();
          }
        } else {
          toast.info('Tüm parklar için görev kaydı zaten mevcut');
        }
      }
    } catch (error) {
      console.error('Günlük görev oluşturma hatası:', error);
      toast.error('Günlük görevler oluşturulamadı');
    } finally {
      setCreatingDailyTasks(false);
    }
  };

  // Toplu hava durumu uygula
  const applyBulkWeather = async () => {
    if (!bulkWeatherSelection) {
      toast.error('Lütfen bir hava durumu seçin');
      return;
    }
    
    try {
      setApplyingBulkWeather(true);
      
      // Hangi parklara uygulanacak - rol kapsamındaki parklar (filteredParksForSettings) ve seçili bölgelere göre
      const scopeParks = filteredParksForSettings || [];
      let targetParks;
      if (bulkWeatherRegions.length === 0) {
        // Hiçbir bölge seçilmemişse yetkili tüm parklar
        targetParks = scopeParks;
      } else {
        // Seçili bölgelerdeki ve yetki kapsamındaki parklar
        targetParks = scopeParks.filter(p => bulkWeatherRegions.includes(p.region_id));
      }
      
      if (targetParks.length === 0) {
        toast.error('Uygulanacak park bulunamadı');
        return;
      }
      
      // Her park için config güncelle
      const promises = targetParks.map(park => 
        axios.put(`${API}/api/parks/${park.id}/daily-tasks-config`, {
          manual_weather: bulkWeatherSelection === 'none' ? null : bulkWeatherSelection
        })
      );
      
      await Promise.all(promises);
      
      // Aktif hava durumunu güncelle (none seçildiyse sıfırla)
      setActiveWeatherStatus(bulkWeatherSelection === 'none' ? null : bulkWeatherSelection);
      
      toast.success(`${targetParks.length} park için hava durumu güncellendi`);
      setBulkWeatherMode(false);
      // bulkWeatherSelection'ı sıfırlama - seçim dialog'da kalıcı olsun
    } catch (error) {
      console.error('Toplu hava durumu güncellenirken hata:', error);
      toast.error('Hava durumu güncellenemedi');
    } finally {
      setApplyingBulkWeather(false);
    }
  };
  
  // Toplu hava durumu iptal (dialog'u kapat ve sıfırla)
  const cancelBulkWeather = () => {
    setBulkWeatherMode(false);
    setBulkWeatherSelection(null);
    setBulkWeatherRegions([]);
  };
  
  // Sadece seçimleri temizle (dialog açık kalır)
  const clearWeatherSelections = () => {
    setBulkWeatherSelection(null);
    setBulkWeatherRegions([]);
  };
  
  // Bölge seçimi toggle
  const toggleWeatherRegion = (regionId) => {
    setBulkWeatherRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(id => id !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };
  
  // Tüm bölgeleri seç/kaldır (yetkili bölgeler: filteredRegionsForSettings)
  const toggleAllWeatherRegions = () => {
    if (bulkWeatherRegions.length === filteredRegionsForSettings.length) {
      setBulkWeatherRegions([]);
    } else {
      setBulkWeatherRegions(filteredRegionsForSettings.map(r => r.id));
    }
  };
  
  // Seçili bölgelerdeki toplam park sayısı (yetkili parklar dahilinde)
  const getSelectedParksCount = () => {
    const scopeParks = filteredParksForSettings || [];
    if (bulkWeatherRegions.length === 0) {
      return scopeParks.length;
    }
    return scopeParks.filter(p => bulkWeatherRegions.includes(p.region_id)).length;
  };
  
  // Park Detayları için Ara butonu işlevi
  const applyOverviewFilters = () => {
    setAppliedOverviewSearch(overviewSearch);
    setAppliedOverviewStatusFilter(overviewStatusFilter);
    setAppliedOverviewRegionFilter(overviewRegionFilter);
    setOverviewPage(1);
  };
  
  // Filtreleri temizle
  const clearOverviewFilters = () => {
    setOverviewSearch("");
    setOverviewStatusFilter("all");
    setOverviewRegionFilter("all");
    setAppliedOverviewSearch("");
    setAppliedOverviewStatusFilter("all");
    setAppliedOverviewRegionFilter("all");
    setOverviewPage(1);
  };
  
  // Hava durumu label helper
  const getWeatherLabel = (key) => {
    const labels = {
      'normal': 'Normal',
      'rainy': 'Yağmurlu',
      'snowy': 'Karlı',
      'hot': 'Aşırı Sıcak',
      'stormy': 'Fırtınalı',
      'foggy': 'Sisli',
      'windy': 'Rüzgarlı'
    };
    return labels[key] || key;
  };
  
  const getWeatherIcon = (key) => {
    const icons = {
      'normal': '☀️',
      'rainy': '🌧️',
      'snowy': '❄️',
      'hot': '🔥',
      'stormy': '⛈️',
      'foggy': '🌫️',
      'windy': '💨'
    };
    return icons[key] || '🌤️';
  };

  const handleParkSelect = (parkId) => {
    setSelectedParkForSettings(parkId);
    loadParkConfig(parkId);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Beklemede', color: 'bg-gray-500' },
      in_progress: { label: 'Devam Ediyor', color: 'bg-blue-500' },
      completed: { label: 'Tamamlandı', color: 'bg-green-500' },
      incomplete: { label: 'Eksik', color: 'bg-red-500' }
    };
    
    const statusInfo = statusMap[status] || statusMap.pending;
    
    return (
      <Badge className={`${statusInfo.color} text-white`}>
        {statusInfo.label}
      </Badge>
    );
  };

  const getCompletionColor = (rate) => {
    if (rate >= 90) return 'text-green-600';
    if (rate >= 70) return 'text-blue-600';
    if (rate >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <TabsContent value="daily-maintenance" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Park Bakım Yönetimi</h2>
          <p className="text-muted-foreground">
            Günlük park bakım görevlerini takip edin
          </p>
        </div>
      </div>

      {/* Tarih Seçici */}
      <Card>
        <CardHeader>
          <CardTitle>Tarih Seçin</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-56">
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                max={today}
                placeholder="Tarih seç"
              />
            </div>
            <Button onClick={loadDailySummary} disabled={loading}>
              {loading ? 'Yükleniyor...' : 'Ara'}
            </Button>
            
            {/* Günlük Görevleri Oluştur Butonu - Sadece Müdür ve Müdür Yardımcısı */}
            {(isMudur || isMudurYardimcisi) && (
              <Button 
                variant="outline"
                onClick={createDailyTasks}
                disabled={creatingDailyTasks}
                className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
              >
                {creatingDailyTasks ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4" />
                    Günlük Görevleri Aktifleştir
                  </>
                )}
              </Button>
            )}
            
            {/* Rapor İndir Butonu */}
            <Button 
              variant="outline" 
              onClick={() => {
                // Rol bazlı parametreler
                let params = new URLSearchParams({ date: selectedDate });
                
                if (isParkGorevlisi) {
                  params.append('personnel_id', personnelId);
                } else if (isFormen) {
                  params.append('formen_id', personnelId);
                } else if (isBolgeMuhendisi) {
                  params.append('bolge_muhendisi_id', personnelId);
                } else if (isMudurYardimcisi) {
                  params.append('mudur_yardimcisi_id', personnelId);
                }
                
                // Dosyayı indir
                window.open(`${API}/api/maintenance/report/${selectedDate}?${params.toString()}`, '_blank');
                toast.success('Rapor indiriliyor...');
              }}
              className="flex items-center gap-2"
            >
              📥 Excel Rapor
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alt Sekmeler */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className={`grid w-full ${
          canAccessMyTasks && canAccessSettings ? 'grid-cols-5' : 
          canAccessMyTasks ? 'grid-cols-4' : 
          (canAccessSettings && canAccessVisualOverview) ? 'grid-cols-4' :
          canAccessSettings ? 'grid-cols-3' : 'grid-cols-2'
        }`}>
          <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
          <TabsTrigger value="map" className="flex items-center gap-1">
            <MapIcon className="h-4 w-4" />
            Harita
          </TabsTrigger>
          {canAccessMyTasks && (
            <TabsTrigger value="tasks">Parktaki Görevlerim</TabsTrigger>
          )}
          {canAccessMyTasks && (
            <TabsTrigger value="substitutions" className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              Vekalet Parklar
            </TabsTrigger>
          )}
          {canAccessVisualOverview && (
            <TabsTrigger value="visual-overview" className="flex items-center gap-1">
              <Image className="h-4 w-4" />
              Görsel Bakış
            </TabsTrigger>
          )}
          {canAccessSettings && (
            <TabsTrigger value="settings">Ayarlar</TabsTrigger>
          )}
        </TabsList>

        {/* Genel Bakış */}
        <SubTabsContent value="overview" className="space-y-4 mt-4">
          {loading ? (
            <div className="text-center py-8">Yükleniyor...</div>
          ) : dailySummary ? (
            <>
              {/* Özet Kartlar - Üst Satır */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Toplam Park</CardTitle>
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dailySummary.summary.total_parks}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Toplam Görev</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dailySummary.summary.total_tasks}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Tamamlanan</CardTitle>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {dailySummary.summary.completed_tasks}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Ortalama Tamamlanma</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${getCompletionColor(dailySummary.summary.avg_completion_rate)}`}>
                      %{dailySummary.summary.avg_completion_rate}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Özet Kartlar - Alt Satır (Detaylı İstatistikler) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <button 
                  onClick={() => openDetailModal("on_time")}
                  className="w-full text-left"
                >
                  <Card className="border-green-200 bg-green-50/50 cursor-pointer hover:shadow-md transition-shadow h-full">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">✅ Zamanında</CardTitle>
                      <ChevronRight className="h-4 w-4 text-green-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">{dailySummary.summary.on_time_tasks || 0}</div>
                      <p className="text-xs text-muted-foreground">görev tamamlandı • Detay için tıkla</p>
                    </CardContent>
                  </Card>
                </button>

                <button 
                  onClick={() => openDetailModal("late_all")}
                  className="w-full text-left"
                >
                  <Card className="border-orange-200 bg-orange-50/50 cursor-pointer hover:shadow-md transition-shadow h-full">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">⚠️ Geç Tamamlanan</CardTitle>
                      <ChevronRight className="h-4 w-4 text-orange-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-orange-600">{dailySummary.summary.late_tasks || 0}</div>
                      <p className="text-xs text-muted-foreground">görev geç yapıldı • Detay için tıkla</p>
                    </CardContent>
                  </Card>
                </button>

                <button 
                  onClick={() => openDetailModal("in_progress")}
                  className="w-full text-left"
                >
                  <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-md transition-shadow h-full">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">🔄 Devam Eden</CardTitle>
                      <ChevronRight className="h-4 w-4 text-blue-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-blue-600">{dailySummary.summary.status_counts?.in_progress || 0}</div>
                      <p className="text-xs text-muted-foreground">park • Detay için tıkla</p>
                    </CardContent>
                  </Card>
                </button>

                <button 
                  onClick={() => openDetailModal("pending")}
                  className="w-full text-left"
                >
                  <Card className="border-gray-200 bg-gray-50/50 cursor-pointer hover:shadow-md transition-shadow h-full">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">⏳ Bekleyen</CardTitle>
                      <ChevronRight className="h-4 w-4 text-gray-600" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-gray-600">{dailySummary.summary.status_counts?.pending || 0}</div>
                      <p className="text-xs text-muted-foreground">park • Detay için tıkla</p>
                    </CardContent>
                  </Card>
                </button>
              </div>
              
              {/* En Çok Atlanan ve Geç Tamamlanan Görevler */}
              {dailySummary.detailed_stats && (
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  {/* En Çok Atlanan Görevler */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-500" />
                        En Çok Atlanan Görevler
                        <span className="text-xs font-normal text-muted-foreground">({formatDateTR(selectedDate)})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dailySummary.detailed_stats.most_skipped_tasks?.length > 0 ? (
                        <div className="space-y-2">
                          {dailySummary.detailed_stats.most_skipped_tasks.map((task, idx) => (
                            <div 
                              key={task.task_id} 
                              className="flex items-center justify-between p-2 rounded bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => openDetailModal("skipped", task.task_id, task.name)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-muted-foreground">{idx + 1}.</span>
                                <span className="text-sm">{task.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="destructive" className="text-xs cursor-pointer">
                                  {task.skipped} atlama
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  / {task.total}
                                </span>
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">Atlanan görev yok</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* En Çok Geç Tamamlanan Görevler */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="h-5 w-5 text-orange-500" />
                        En Çok Geç Tamamlanan Görevler
                        <span className="text-xs font-normal text-muted-foreground">({formatDateTR(selectedDate)})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dailySummary.detailed_stats.most_late_tasks?.filter(t => t.late > 0).length > 0 ? (
                        <div className="space-y-2">
                          {dailySummary.detailed_stats.most_late_tasks.filter(t => t.late > 0).map((task, idx) => (
                            <div 
                              key={task.task_id} 
                              className="flex items-center justify-between p-2 rounded bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                              onClick={() => openDetailModal("late", task.task_id, task.name)}
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-muted-foreground">{idx + 1}.</span>
                                <span className="text-sm">{task.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="text-xs bg-orange-500 cursor-pointer">
                                  {task.late} geç
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  / {task.completed} tamamlanan
                                </span>
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">Geç tamamlanan görev yok 🎉</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
              
              {/* 📊 Performans Grafikleri kaldırıldı */}

              {/* Toplu Hava Durumu Ayarı (Formen/BM/MY: kendi bölgeleri, Müdür: tüm bölgeler) */}
              {canAccessSettings && (
                <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🌤️</span>
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            Toplu Hava Durumu Ayarı
                            {activeWeatherStatus && (
                              <Badge className="bg-amber-500 text-white text-xs">
                                {getWeatherIcon(activeWeatherStatus)} {getWeatherLabel(activeWeatherStatus)} seçilmiş
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs">Tüm parklar veya seçili bölge için tek seferde ayarlayın</CardDescription>
                        </div>
                      </div>
                      <Button 
                        variant={bulkWeatherMode ? "outline" : "outline"}
                        size="sm"
                        onClick={() => bulkWeatherMode ? clearWeatherSelections() : setBulkWeatherMode(true)}
                        className={bulkWeatherMode ? "text-red-600 border-red-300 hover:bg-red-50" : ""}
                      >
                        {bulkWeatherMode ? '🗑️ Seçimi Temizle' : '⚡ Toplu Ayarla'}
                      </Button>
                    </div>
                  </CardHeader>
                  
                  {bulkWeatherMode && (
                    <CardContent className="pt-0">
                      <div className="space-y-4">
                        {/* Çoklu Bölge Seçimi */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium">Hedef Bölgeler</label>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 text-xs"
                              onClick={toggleAllWeatherRegions}
                            >
                              {bulkWeatherRegions.length === filteredRegionsForSettings.length ? '✕ Tümünü Kaldır' : '✓ Tümünü Seç'}
                            </Button>
                          </div>
                          
                          {/* Seçili bölge sayısı göstergesi */}
                          <div className="mb-2">
                            <Badge variant="outline" className={`text-xs ${bulkWeatherRegions.length === 0 ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-amber-50 text-amber-700 border-amber-300'}`}>
                              {bulkWeatherRegions.length === 0 
                                ? `🌍 Yetkinizdeki tüm parklar (${(filteredParksForSettings || []).length} park)` 
                                : `📍 ${bulkWeatherRegions.length} bölge seçili (${getSelectedParksCount()} park)`}
                            </Badge>
                          </div>
                          
                          {/* Bölge checkbox listesi - rol bazlı: Formen/BM/MY kendi bölgeleri, Müdür tümü */}
                          <div className="max-h-40 overflow-y-auto border rounded-lg p-2 bg-white">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                              {filteredRegionsForSettings.map((region) => {
                                const regionParkCount = (filteredParksForSettings || []).filter(p => p.region_id === region.id).length;
                                const isSelected = bulkWeatherRegions.includes(region.id);
                                return (
                                  <button
                                    key={region.id}
                                    type="button"
                                    onClick={() => toggleWeatherRegion(region.id)}
                                    className={`flex items-center gap-2 p-2 rounded-md border text-left text-sm transition-all ${
                                      isSelected 
                                        ? 'border-amber-500 bg-amber-50 text-amber-800' 
                                        : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                                    }`}
                                  >
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                      isSelected ? 'border-amber-500 bg-amber-500' : 'border-gray-300'
                                    }`}>
                                      {isSelected && <span className="text-white text-xs">✓</span>}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span className="block truncate font-medium">{region.name}</span>
                                      <span className="text-xs text-muted-foreground">{regionParkCount} park</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            💡 Hiçbir bölge seçilmezse yetkinizdeki tüm parklara uygulanır
                          </p>
                        </div>
                        
                        {/* Hava Durumu Seçimi */}
                        <div>
                          <label className="text-sm font-medium block mb-2">Hava Durumu</label>
                          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                            {[
                              { key: 'normal', label: 'Normal', icon: '☀️' },
                              { key: 'rainy', label: 'Yağmurlu', icon: '🌧️' },
                              { key: 'snowy', label: 'Karlı', icon: '❄️' },
                              { key: 'hot', label: 'Aşırı Sıcak', icon: '🔥' },
                              { key: 'stormy', label: 'Fırtınalı', icon: '⛈️' },
                              { key: 'foggy', label: 'Sisli', icon: '🌫️' },
                              { key: 'windy', label: 'Rüzgarlı', icon: '💨' },
                              { key: 'none', label: 'Sıfırla', icon: '🔄' }
                            ].map((weather) => (
                              <button
                                key={weather.key}
                                type="button"
                                onClick={() => setBulkWeatherSelection(weather.key)}
                                className={`p-2 rounded-lg border-2 text-center transition-all ${
                                  bulkWeatherSelection === weather.key
                                    ? 'border-amber-500 bg-amber-100 scale-105' 
                                    : 'border-gray-200 bg-white hover:border-amber-300'
                                }`}
                              >
                                <span className="text-xl block">{weather.icon}</span>
                                <span className="text-xs mt-1 block truncate">{weather.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Uygula Butonu */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <p className="text-sm text-muted-foreground">
                            {getSelectedParksCount()} parka uygulanacak
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline"
                              onClick={cancelBulkWeather}
                            >
                              İptal
                            </Button>
                            <Button 
                              onClick={applyBulkWeather}
                              disabled={!bulkWeatherSelection || applyingBulkWeather}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              {applyingBulkWeather ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Uygulanıyor...
                                </>
                              ) : (
                                '✓ Toplu Uygula'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Park Listesi - Filtreleme ve Sayfalama */}
              <div className="space-y-4">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      Park Detayları 
                      <span className="text-sm font-normal text-muted-foreground ml-2">({formatDateTR(selectedDate)})</span>
                    </h3>
                    
                    {/* Aktif Filtreler Badge */}
                    {(appliedOverviewSearch || appliedOverviewStatusFilter !== 'all' || appliedOverviewRegionFilter !== 'all') && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Aktif Filtreler:</span>
                        {appliedOverviewSearch && (
                          <Badge variant="secondary" className="text-xs">
                            🔍 &quot;{appliedOverviewSearch}&quot;
                          </Badge>
                        )}
                        {appliedOverviewStatusFilter !== 'all' && (
                          <Badge variant="secondary" className="text-xs">
                            {appliedOverviewStatusFilter === 'completed' ? '✅ Tamamlandı' : 
                             appliedOverviewStatusFilter === 'in_progress' ? '🔄 Devam Ediyor' : '⏳ Beklemede'}
                          </Badge>
                        )}
                        {appliedOverviewRegionFilter !== 'all' && (
                          <Badge variant="secondary" className="text-xs">
                            📍 {filteredRegionsForSettings.find(r => r.id === appliedOverviewRegionFilter)?.name}
                          </Badge>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-xs text-red-500 hover:text-red-700"
                          onClick={clearOverviewFilters}
                        >
                          ✕ Temizle
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Arama ve Filtreler */}
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Arama */}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Park veya personel ara..."
                        value={overviewSearch}
                        onChange={(e) => setOverviewSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyOverviewFilters()}
                        className="pl-8 h-9 w-48"
                      />
                    </div>
                    
                    {/* Durum Filtresi */}
                    <SearchableSelect
                      value={overviewStatusFilter}
                      onChange={setOverviewStatusFilter}
                      allLabel="Tüm Durumlar"
                      searchPlaceholder="Durum ara..."
                      className="w-36 h-9"
                      options={[
                        { id: "completed", name: "✅ Tamamlandı" },
                        { id: "in_progress", name: "🔄 Devam Ediyor" },
                        { id: "pending", name: "⏳ Beklemede" },
                      ]}
                    />

                    {/* Bölge Filtresi */}
                    <SearchableSelect
                      value={overviewRegionFilter}
                      onChange={setOverviewRegionFilter}
                      allLabel={isMudur ? 'Tüm Bölgeler' : 'Bölgelerim'}
                      searchPlaceholder="Bölge ara..."
                      className="w-36 h-9"
                      options={filteredRegionsForSettings.map((region) => ({ id: region.id, name: region.name }))}
                    />
                    
                    {/* Sıralama */}
                    <SearchableSelect
                      value={overviewSortBy}
                      onChange={setOverviewSortBy}
                      className="w-36 h-9"
                      showAllOption={false}
                      placeholder="Sırala"
                      searchPlaceholder="Sırala ara..."
                      options={[
                        { id: 'name', name: '📝 Park Adı' },
                        { id: 'completion_asc', name: '📊 Oran (Düşük)' },
                        { id: 'completion_desc', name: '📊 Oran (Yüksek)' },
                        { id: 'status', name: '📋 Durum' },
                      ]}
                    />
                    
                    {/* Ara Butonu */}
                    <Button 
                      onClick={applyOverviewFilters}
                      className="h-9 bg-blue-600 hover:bg-blue-700"
                    >
                      <Search className="h-4 w-4 mr-1" />
                      Ara
                    </Button>
                  </div>
                </div>
                
                {dailySummary.task_completions.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      Bu tarih için görev kaydı bulunmamaktadır
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Filtrelenmiş ve Sayfalanmış Liste */}
                    {(() => {
                      // Filtreleme - applied değişkenlerini kullan (Ara butonuna basınca aktif olur)
                      let filtered = dailySummary.task_completions.filter(c => {
                        const searchLower = normalizeSearchText(appliedOverviewSearch);
                        const matchesSearch = !searchLower ||
                          normalizeSearchText(c.park_name).includes(searchLower) ||
                          normalizeSearchText(c.personnel_name).includes(searchLower);
                        const matchesStatus = appliedOverviewStatusFilter === 'all' || c.status === appliedOverviewStatusFilter;
                        // Bölge filtresi için park bilgisine ihtiyaç var
                        const park = parks.find(p => p.id === c.park_id);
                        const matchesRegion = appliedOverviewRegionFilter === 'all' || park?.region_id === appliedOverviewRegionFilter;
                        return matchesSearch && matchesStatus && matchesRegion;
                      });
                      
                      // Sıralama
                      filtered.sort((a, b) => {
                        switch(overviewSortBy) {
                          case 'name': return a.park_name?.localeCompare(b.park_name, 'tr');
                          case 'completion_asc': return a.completion_rate - b.completion_rate;
                          case 'completion_desc': return b.completion_rate - a.completion_rate;
                          case 'status':
                            const statusOrder = { completed: 3, in_progress: 2, pending: 1 };
                            return (statusOrder[b.status] || 0) - (statusOrder[a.status] || 0);
                          default: return 0;
                        }
                      });
                      
                      // Sayfalama
                      const totalFiltered = filtered.length;
                      const totalPages = Math.ceil(totalFiltered / overviewPageSize);
                      const paginatedData = filtered.slice((overviewPage - 1) * overviewPageSize, overviewPage * overviewPageSize);
                      
                      return (
                        <>
                          {/* Sonuç Özeti */}
                          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                            <span>
                              Toplam {totalFiltered} park gösteriliyor 
                              {totalFiltered !== dailySummary.task_completions.length && ` (${dailySummary.task_completions.length} parktan)`}
                            </span>
                            <span>Sayfa {overviewPage} / {totalPages || 1}</span>
                          </div>
                          
                          {/* Kompakt Tablo Görünümü */}
                          <Card>
                            <CardContent className="p-0">
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-slate-50 border-b">
                                    <tr>
                                      <th className="text-left p-3 text-sm font-medium">Park</th>
                                      <th className="text-left p-3 text-sm font-medium">Personel</th>
                                      <th className="text-center p-3 text-sm font-medium">Tamamlanma</th>
                                      <th className="text-center p-3 text-sm font-medium">Zamanında</th>
                                      <th className="text-center p-3 text-sm font-medium">Durum</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length === 0 ? (
                                      <tr>
                                        <td colSpan={5} className="text-center py-8 text-muted-foreground">
                                          Filtrelere uygun park bulunamadı
                                        </td>
                                      </tr>
                                    ) : (
                                      paginatedData.map((completion) => (
                                        <tr 
                                          key={completion.id} 
                                          className="border-b hover:bg-blue-50 cursor-pointer transition-colors"
                                          onClick={() => loadParkTaskDetails(completion)}
                                          title="Görev detaylarını görmek için tıklayın"
                                        >
                                          <td className="p-3">
                                            <div className="flex items-center gap-2">
                                              <p className="font-medium text-blue-700 hover:text-blue-900">{completion.park_name}</p>
                                              <ChevronRight className="h-4 w-4 text-blue-400" />
                                            </div>
                                          </td>
                                          <td className="p-3 text-sm text-muted-foreground">
                                            {(() => {
                                              const byNames = [...new Set((completion.tasks || []).filter(t => t.completed && t.completed_by_name).map(t => t.completed_by_name))];
                                              if (byNames.length > 0) {
                                                return (
                                                  <span>
                                                    <span>{completion.personnel_name}</span>
                                                    <span className="text-gray-400 mx-1">·</span>
                                                    <span className="text-amber-600 font-medium">{byNames.join(', ')} (vekaleten)</span>
                                                  </span>
                                                );
                                              }
                                              return completion.personnel_name;
                                            })()}
                                          </td>
                                          <td className="p-3 text-center">
                                            <span className={`font-bold ${getCompletionColor(completion.completion_rate)}`}>
                                              %{completion.completion_rate}
                                            </span>
                                            <span className="text-xs text-muted-foreground ml-1">
                                              ({completion.completed_tasks}/{completion.total_tasks})
                                            </span>
                                          </td>
                                          <td className="p-3 text-center">
                                            <span className="font-medium text-blue-600">%{completion.on_time_rate}</span>
                                          </td>
                                          <td className="p-3 text-center">
                                            {completion.status === 'completed' ? (
                                              <Badge className="bg-green-500">Tamamlandı</Badge>
                                            ) : completion.status === 'in_progress' ? (
                                              <Badge className="bg-blue-500">Devam Ediyor</Badge>
                                            ) : (
                                              <Badge variant="secondary">Beklemede</Badge>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </CardContent>
                          </Card>
                          
                          {/* Sayfalama Kontrolleri */}
                          {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-4">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setOverviewPage(1)}
                                disabled={overviewPage === 1}
                              >
                                «
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setOverviewPage(p => Math.max(1, p - 1))}
                                disabled={overviewPage === 1}
                              >
                                ‹ Önceki
                              </Button>
                              
                              {/* Sayfa numaraları */}
                              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (overviewPage <= 3) {
                                  pageNum = i + 1;
                                } else if (overviewPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = overviewPage - 2 + i;
                                }
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={overviewPage === pageNum ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setOverviewPage(pageNum)}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                              
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setOverviewPage(p => Math.min(totalPages, p + 1))}
                                disabled={overviewPage === totalPages}
                              >
                                Sonraki ›
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setOverviewPage(totalPages)}
                                disabled={overviewPage === totalPages}
                              >
                                »
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Veri yüklenemedi
              </CardContent>
            </Card>
          )}
        </SubTabsContent>

        {/* HARİTA SEKMESİ */}
        <SubTabsContent value="map" className="space-y-4 mt-4">
          {isOnLeave && canAccessMyTasks ? (
            <Card className="border border-amber-200 bg-amber-50">
              <CardContent className="py-6 text-center">
                <AlertCircle className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                <p className="font-semibold text-amber-800">İzinli olduğunuz için konum takibi pasif</p>
                <p className="text-sm text-amber-700 mt-1">
                  İzin tarihleri boyunca harita ve konum takibi görünmez.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
            {/* Filtreler */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4 items-center">
                {/* Bakım Durumu Filtresi */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Bakım Durumu:</Label>
                  <SearchableSelect
                    value={tempMapStatusFilter}
                    onChange={setTempMapStatusFilter}
                    allLabel="Tümü"
                    searchPlaceholder="Durum ara..."
                    className="w-[180px]"
                    options={[
                      { id: "completed_today", name: "🟢 Bugün Yapıldı" },
                      { id: "recent", name: "🟡 Son 3 Günde" },
                      { id: "pending", name: "🔴 Bakım Bekliyor" },
                    ]}
                  />
                </div>

                {/* Bölge Filtresi */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Bölge:</Label>
                  <SearchableSelect
                    value={tempMapRegionFilter}
                    onChange={setTempMapRegionFilter}
                    allLabel="Tüm Bölgeler"
                    searchPlaceholder="Bölge ara..."
                    className="w-[180px]"
                    options={regions.map((region) => ({ id: region.id, name: region.name }))}
                  />
                </div>

                {/* Park Filtresi (aranabilir — örn. Zafer Parkı) */}
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">Park:</Label>
                  <SearchableSelect
                    value={tempMapParkFilter}
                    onChange={setTempMapParkFilter}
                    allLabel="Tüm Parklar"
                    searchPlaceholder="Park ara (örn. Zafer)..."
                    className="w-[200px]"
                    options={(filteredParksForSettings || [])
                      .filter(p => tempMapRegionFilter === "all" || p.region_id === tempMapRegionFilter)
                      .slice()
                      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "tr"))
                      .map(p => ({ id: p.id, name: p.name }))}
                  />
                </div>

                {/* Ara ve Temizle Butonları */}
                <div className="flex gap-2">
                  <Button 
                    onClick={applyMapFilters}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    size="sm"
                  >
                    <Search className="h-4 w-4 mr-1" />
                    Ara
                  </Button>
                  <Button 
                    onClick={clearMapFilters}
                    variant="outline"
                    size="sm"
                  >
                    Temizle
                  </Button>
                </div>
                
                {/* Park Sayısı */}
                <div className="ml-auto text-sm text-gray-500">
                  <Badge variant="outline" className="text-sm">
                    {filteredParksForMap.length} park gösteriliyor
                  </Badge>
                </div>
              </div>
              </CardContent>
            </Card>
          
          {/* Renk Açıklaması */}
          <div className="flex flex-wrap gap-4 px-2">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white shadow"></div>
              <span className="text-sm">Bugün bakım yapıldı</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500 border-2 border-white shadow"></div>
              <span className="text-sm">Son 3 günde bakım yapıldı</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow"></div>
              <span className="text-sm">Bakım bekliyor (3+ gün)</span>
            </div>
          </div>
          
          {/* Harita */}
          <Card>
            <CardContent className="p-0">
              <div style={{ height: '500px', width: '100%', position: 'relative' }}>
                {filteredParksForMap.length > 0 ? (
                  <>
                  <MapLayerToggle value={mapLayer} onChange={setMapLayer} floating />
                  <MapContainer
                    center={mapCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%', borderRadius: '0 0 8px 8px' }}
                  >
                    <MapLayerTile layer={mapLayer} />
                    
                    {filteredParksForMap.map((park) => {
                      const parkSummary = dailySummary?.parks?.find(p => p.park_id === park.id);
                      const status = getParkMaintenanceStatus(parkSummary);

                      // Parka atanan personeli rolüne göre ayır: formen rolündeki
                      // kişiler "Görevli" listesine değil "Formen" satırına yazılır.
                      // (assigned_personnel_ids cache'i geçmişte formenleri de içerdiği
                      // için isim listesini doğrudan basmak formeni görevli gösteriyordu.)
                      const gorevliSet = new Set();
                      const formenSet = new Set();
                      (park.assigned_personnel_ids || []).forEach((pid) => {
                        const person = personnel.find(p => p.id === pid);
                        if (!person) return;
                        if (person.role === 'formen') {
                          // Doğrudan parka atanmış formen → Görevli değil, Formen.
                          formenSet.add(person.name);
                          return;
                        }
                        // Park görevlisi → Görevli; bağlı olduğu formeni de Formen satırına ekle.
                        gorevliSet.add(person.name);
                        let formen = null;
                        if (person.formen_id) {
                          formen = personnel.find(p => p.id === person.formen_id);
                        } else if (person.parent_id) {
                          const parent = personnel.find(p => p.id === person.parent_id);
                          if (parent?.role === 'formen') formen = parent;
                        }
                        if (formen?.name) formenSet.add(formen.name);
                      });
                      // Personnel listesinde eşleşme bulunamazsa isim cache'ine düş.
                      const gorevliNames = gorevliSet.size
                        ? Array.from(gorevliSet)
                        : (park.assigned_personnel_names || []);
                      const formenNames = Array.from(formenSet);

                      return (
                        <React.Fragment key={park.id}>
                          {/* Park Marker */}
                          <Marker
                            position={[park.latitude, park.longitude]}
                            icon={createMaintenanceIcon(status)}
                          >
                            <LeafletTooltip direction="top" offset={[0, -12]}>
                              <span className="font-medium">{park.name}</span>
                            </LeafletTooltip>
                            <Popup>
                              <div className="min-w-[200px]">
                                <h3 className="font-bold text-sm mb-1">{park.name}</h3>
                                <div className="text-xs space-y-1">
                                  <p><strong>Bölge:</strong> {regions.find(r => r.id === park.region_id)?.name || '-'}</p>
                                  <p><strong>Görevli:</strong> {gorevliNames.length ? gorevliNames.join(', ') : '-'}</p>
                                  <p><strong>Formen:</strong> {formenNames.length ? formenNames.join(', ') : '-'}</p>
                                  <p><strong>Alan:</strong> {park.area != null ? `${park.area.toLocaleString('tr-TR')} m²` : '-'}</p>
                                  <hr className="my-1" />
                                  <p className="font-medium">
                                    <strong>Bakım Durumu:</strong>{' '}
                                    {status === 'completed_today' && <span className="text-green-600">✅ Bugün yapıldı</span>}
                                    {status === 'recent' && <span className="text-yellow-600">🟡 Son 3 günde</span>}
                                    {status === 'pending' && <span className="text-red-600">❌ Bakım bekliyor</span>}
                                  </p>
                                  {parkSummary?.last_completed_date && (
                                    <p><strong>Son Bakım:</strong> {new Date(parkSummary.last_completed_date).toLocaleDateString('tr-TR')}</p>
                                  )}
                                  {parkSummary && (
                                    <p><strong>Görevler:</strong> {parkSummary.completed_tasks}/{parkSummary.total_tasks}</p>
                                  )}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                          
                          {/* Park Sınırları (varsa) */}
                          {park.boundaries && park.boundaries.length > 2 && (
                            <Polygon
                              positions={park.boundaries}
                              pathOptions={{
                                color: status === 'completed_today' ? '#22c55e' : status === 'recent' ? '#eab308' : '#ef4444',
                                fillColor: status === 'completed_today' ? '#22c55e' : status === 'recent' ? '#eab308' : '#ef4444',
                                fillOpacity: 0.2,
                                weight: 2
                              }}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </MapContainer>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-500">
                    <MapIcon className="h-16 w-16 mb-4 opacity-20" />
                    <p>Seçilen filtrelere uygun park bulunamadı</p>
                    <p className="text-sm mt-1">Koordinatı olan parklar haritada gösterilir</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* İstatistikler */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {filteredParksForSettings.filter(park => {
                    const ps = dailySummary?.parks?.find(p => p.park_id === park.id);
                    return getParkMaintenanceStatus(ps) === 'completed_today';
                  }).length}
                </div>
                <p className="text-sm text-green-700">Bugün Bakım Yapıldı</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {filteredParksForSettings.filter(park => {
                    const ps = dailySummary?.parks?.find(p => p.park_id === park.id);
                    return getParkMaintenanceStatus(ps) === 'recent';
                  }).length}
                </div>
                <p className="text-sm text-yellow-700">Son 3 Günde</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {filteredParksForSettings.filter(park => {
                    const ps = dailySummary?.parks?.find(p => p.park_id === park.id);
                    return getParkMaintenanceStatus(ps) === 'pending';
                  }).length}
                </div>
                <p className="text-sm text-red-700">Bakım Bekliyor</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Koordinatı Eksik Parklar Uyarısı */}
          {parksWithoutCoordinates.length > 0 && (
            <Card className="bg-orange-50 border-orange-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-orange-800 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Koordinatı Eksik Parklar ({parksWithoutCoordinates.length} adet)
                </CardTitle>
                <CardDescription className="text-orange-600 text-xs">
                  Bu parklar haritada gösterilemiyor. Park ismine tıklayarak koordinat ekleyebilirsiniz.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-40 overflow-y-auto">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {parksWithoutCoordinates.map(park => (
                      <button 
                        key={park.id} 
                        className="text-xs bg-white px-2 py-1.5 rounded border border-orange-200 truncate hover:bg-orange-100 hover:border-orange-400 cursor-pointer transition-colors text-left flex items-center gap-1"
                        title={`${park.name} - Koordinat eklemek için tıklayın`}
                        onClick={() => {
                          if (onNavigateToPark) {
                            onNavigateToPark(park.id);
                          }
                        }}
                      >
                        <MapPin className="h-3 w-3 text-orange-500 flex-shrink-0" />
                        <span className="truncate">{park.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
            </>
          )}
        </SubTabsContent>

        {/* Görevlerim */}
        <SubTabsContent value="tasks" className="space-y-4 mt-4">
          {isOnLeave && canAccessMyTasks && (
            <Card className="border border-amber-200 bg-amber-50">
              <CardContent className="py-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Sadece görüntüleme (izinlisiniz).</span> Parklarınız ve vekilin yaptığı görevler burada görünür; tamamlama veya düzenleme yapılamaz.
                </p>
              </CardContent>
            </Card>
          )}
          <>
            {/* Yenile Butonu */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadMyTasks}
                disabled={loadingMyTasks}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingMyTasks ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
            </div>

          {loadingMyTasks ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Görevler yükleniyor...</p>
              </CardContent>
            </Card>
          ) : !myTasks || (!Array.isArray(myTasks.parks) && !Array.isArray(myTasks.substitution_parks)) || (myTasks.parks?.length === 0 && myTasks.substitution_parks?.length === 0) ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MapPin className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Atanmış Park Bulunamadı</h3>
                <p className="text-muted-foreground">
                  {personnelId 
                    ? 'Henüz size atanmış bir park veya vekalet verilen park bulunmamaktadır.' 
                    : 'Görevleri görmek için giriş yapın.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Özet Kartları */}
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-blue-700">Toplam Park</p>
                      <p className="text-3xl font-bold text-blue-900">{myTasks.total_parks ?? 0}</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-amber-700">Toplam Görev</p>
                      <p className="text-3xl font-bold text-amber-900">
                        {[...(Array.isArray(myTasks.parks) ? myTasks.parks : []), ...(Array.isArray(myTasks.substitution_parks) ? myTasks.substitution_parks : [])].reduce((sum, p) => sum + (p.task_completion?.total_tasks || 0), 0)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-green-700">Tamamlanan</p>
                      <p className="text-3xl font-bold text-green-900">
                        {[...(Array.isArray(myTasks.parks) ? myTasks.parks : []), ...(Array.isArray(myTasks.substitution_parks) ? myTasks.substitution_parks : [])].reduce((sum, p) => sum + (p.task_completion?.completed_tasks || 0), 0)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Kendi Parklarım */}
              {(Array.isArray(myTasks.parks) ? myTasks.parks : []).length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-green-600" />
                    Kendi Parklarım
                  </h3>
              {(Array.isArray(myTasks.parks) ? myTasks.parks : []).map((park) => {
                const taskCompletion = park.task_completion;
                const groupedTasks = groupTasksByTimeSlot(taskCompletion?.tasks);
                const isExpanded = expandedParks[park.park_id];
                const completionPercent = taskCompletion?.completion_rate || 0;
                
                return (
                  <Card key={park.park_id} className="overflow-hidden">
                    {/* Park Başlığı */}
                    <div 
                      className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b cursor-pointer hover:from-slate-100 hover:to-slate-200 transition-colors"
                      onClick={() => toggleParkExpand(park.park_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <MapPin className="h-5 w-5 text-green-600" />
                          <div>
                            <h3 className="font-semibold text-lg">{park.park_name}</h3>
                            {park.park_address && (
                              <p className="text-sm text-muted-foreground">{park.park_address}</p>
                            )}
                            {isOnLeave && parkDeputyByParkId[park.park_id] && (
                              <p className="text-sm text-amber-700 mt-1 font-medium">
                                📞 Vekil: {parkDeputyByParkId[park.park_id]}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">Tamamlanma</p>
                            <p className={`text-xl font-bold ${
                              completionPercent >= 80 ? 'text-green-600' :
                              completionPercent >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              %{completionPercent.toFixed(0)}
                            </p>
                          </div>
                          <Badge variant={
                            taskCompletion?.status === 'completed' ? 'default' :
                            taskCompletion?.status === 'in_progress' ? 'secondary' : 'outline'
                          } className={
                            taskCompletion?.status === 'completed' ? 'bg-green-500' :
                            taskCompletion?.status === 'in_progress' ? 'bg-blue-500 text-white' : ''
                          }>
                            {taskCompletion?.status === 'completed' ? 'Tamamlandı' :
                             taskCompletion?.status === 'in_progress' ? 'Devam Ediyor' : 'Beklemede'}
                          </Badge>
                          <Button variant="ghost" size="sm">
                            {isExpanded ? '▲' : '▼'}
                          </Button>
                        </div>
                      </div>
                      
                      {/* İlerleme Çubuğu */}
                      <div className="mt-3">
                        <Progress value={completionPercent} className="h-2" />
                        <p className="text-xs text-muted-foreground mt-1">
                          {taskCompletion?.completed_tasks || 0} / {taskCompletion?.total_tasks || 0} görev tamamlandı
                        </p>
                      </div>
                    </div>
                    
                    {/* Görev Listesi */}
                    {isExpanded && (
                      <CardContent className="pt-4">
                        <div className="space-y-6">
                          {/* Sabah Mıntıkası */}
                          {groupedTasks.morning.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Sun className="h-5 w-5 text-yellow-500" />
                                <h4 className="font-medium">Sabah Mıntıkası</h4>
                                <Badge variant="outline" className="text-xs">
                                  {groupedTasks.morning[0]?.expected_time_range?.join(' - ')}
                                </Badge>
                              </div>
                              <div className="space-y-2 pl-7">
                                {groupedTasks.morning.map((task) => (
                                  <TaskItem 
                                    key={task.task_id}
                                    task={task}
                                    parkId={park.park_id}
                                    personnelId={personnelId}
                                    date={selectedDate}
                                    onComplete={handleTaskComplete}
                                    isCompleting={completingTask === `${park.park_id}-${task.task_id}`}
                                    weatherCondition={park.manual_weather}
                                    onMediaCapture={(t) => handleMediaCapture(park.park_id, t)}
                                    readOnly={isOnLeave}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Öğle Mıntıkası */}
                          {groupedTasks.noon.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Cloud className="h-5 w-5 text-orange-500" />
                                <h4 className="font-medium">Öğle Mıntıkası</h4>
                                <Badge variant="outline" className="text-xs">
                                  {groupedTasks.noon[0]?.expected_time_range?.join(' - ')}
                                </Badge>
                              </div>
                              <div className="space-y-2 pl-7">
                                {groupedTasks.noon.map((task) => (
                                  <TaskItem 
                                    key={task.task_id}
                                    task={task}
                                    parkId={park.park_id}
                                    personnelId={personnelId}
                                    date={selectedDate}
                                    onComplete={handleTaskComplete}
                                    isCompleting={completingTask === `${park.park_id}-${task.task_id}`}
                                    weatherCondition={park.manual_weather}
                                    onMediaCapture={(t) => handleMediaCapture(park.park_id, t)}
                                    readOnly={isOnLeave}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Akşam Mıntıkası */}
                          {groupedTasks.evening.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Moon className="h-5 w-5 text-blue-500" />
                                <h4 className="font-medium">Akşam Mıntıkası</h4>
                                <Badge variant="outline" className="text-xs">
                                  {groupedTasks.evening[0]?.expected_time_range?.join(' - ')}
                                </Badge>
                              </div>
                              <div className="space-y-2 pl-7">
                                {groupedTasks.evening.map((task) => (
                                  <TaskItem 
                                    key={task.task_id}
                                    task={task}
                                    parkId={park.park_id}
                                    personnelId={personnelId}
                                    date={selectedDate}
                                    onComplete={handleTaskComplete}
                                    isCompleting={completingTask === `${park.park_id}-${task.task_id}`}
                                    weatherCondition={park.manual_weather}
                                    onMediaCapture={(t) => handleMediaCapture(park.park_id, t)}
                                    readOnly={isOnLeave}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Genel Görevler */}
                          {groupedTasks.general.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <CheckCircle2 className="h-5 w-5 text-gray-500" />
                                <h4 className="font-medium">Genel Görevler</h4>
                              </div>
                              <div className="space-y-2 pl-7">
                                {groupedTasks.general.map((task) => (
                                  <TaskItem 
                                    key={task.task_id}
                                    task={task}
                                    parkId={park.park_id}
                                    personnelId={personnelId}
                                    date={selectedDate}
                                    onComplete={handleTaskComplete}
                                    isCompleting={completingTask === `${park.park_id}-${task.task_id}`}
                                    weatherCondition={park.manual_weather}
                                    onMediaCapture={(t) => handleMediaCapture(park.park_id, t)}
                                    readOnly={isOnLeave}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
                </div>
              )}

              {Array.isArray(myTasks.substitution_parks) && myTasks.substitution_parks.length > 0 && (
                <Card className="border-indigo-200 bg-indigo-50/30">
                  <CardContent className="py-3">
                    <p className="text-sm text-indigo-800">
                      Vekalet parkları ayrı bir deneyim için <span className="font-semibold">Vekalet Parklar</span> sekmesine taşındı.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Medya Yakalama Modal */}
          <MediaCapture
            isOpen={mediaCaptureOpen}
            onClose={() => {
              setMediaCaptureOpen(false);
              setSelectedTaskForMedia(null);
              setSelectedParkIdForMedia(null);
            }}
            parkId={selectedParkIdForMedia}
            personnelId={personnelId}
            taskId={selectedTaskForMedia?.task_id}
            taskName={selectedTaskForMedia?.task_name}
            date={selectedDate}
            mediaType={selectedTaskForMedia?.media_type || 'both'}
            maxVideos={3}
            maxPhotos={5}
            onUploadComplete={handleMediaUploadComplete}
          />
          </>

        </SubTabsContent>

        {/* Vekalet Sekmesi (Park Görevlisi ve Çim Ekibi) */}
        {canAccessMyTasks && (
          <SubTabsContent value="substitutions" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMyTasks}
                disabled={loadingMyTasks}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingMyTasks ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
            </div>

            {loadingMyTasks ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-muted-foreground" />
                  <p className="text-muted-foreground">Vekalet parkları yükleniyor...</p>
                </CardContent>
              </Card>
            ) : Array.isArray(myTasks?.substitution_parks) && myTasks.substitution_parks.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Başka personelin izinli olduğu dönemde sizin baktığınız parklar. Görevleri buradan tamamlayabilirsiniz; vekalet veren personel kimin yaptığını görür.
                </p>
                {myTasks.substitution_parks.map((park) => {
                  const taskCompletion = park.task_completion;
                  const groupedTasks = groupTasksByTimeSlot(taskCompletion?.tasks);
                  const isExpanded = expandedParks[park.park_id];
                  const completionPercent = taskCompletion?.completion_rate || 0;
                  return (
                    <Card key={park.park_id} className="overflow-hidden border-indigo-200 bg-indigo-50/30">
                      <div
                        className="p-4 bg-gradient-to-r from-indigo-50 to-indigo-100 border-b cursor-pointer hover:from-indigo-100 hover:to-indigo-200 transition-colors"
                        onClick={() => toggleParkExpand(park.park_id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <MapPin className="h-5 w-5 text-indigo-600" />
                            <div>
                              <h3 className="font-semibold text-lg">{park.park_name}</h3>
                              <p className="text-sm text-indigo-600">Vekalet: {park.original_personnel_name}</p>
                              {park.park_address && <p className="text-sm text-muted-foreground">{park.park_address}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Tamamlanma</p>
                              <p className={`text-xl font-bold ${completionPercent >= 80 ? 'text-green-600' : completionPercent >= 50 ? 'text-amber-600' : 'text-red-600'}`}>%{completionPercent.toFixed(0)}</p>
                            </div>
                            <Badge variant={taskCompletion?.status === 'completed' ? 'default' : taskCompletion?.status === 'in_progress' ? 'secondary' : 'outline'} className={taskCompletion?.status === 'completed' ? 'bg-green-500' : taskCompletion?.status === 'in_progress' ? 'bg-blue-500 text-white' : ''}>
                              {taskCompletion?.status === 'completed' ? 'Tamamlandı' : taskCompletion?.status === 'in_progress' ? 'Devam Ediyor' : 'Beklemede'}
                            </Badge>
                            <Button variant="ghost" size="sm">{isExpanded ? '▲' : '▼'}</Button>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Progress value={completionPercent} className="h-2" />
                          <p className="text-xs text-muted-foreground mt-1">{taskCompletion?.completed_tasks || 0} / {taskCompletion?.total_tasks || 0} görev tamamlandı</p>
                        </div>
                      </div>
                      {isExpanded && (
                        <CardContent className="pt-4">
                          <div className="space-y-6">
                            {groupedTasks.morning.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-3"><Sun className="h-5 w-5 text-yellow-500" /><h4 className="font-medium">Sabah Mıntıkası</h4><Badge variant="outline" className="text-xs">{groupedTasks.morning[0]?.expected_time_range?.join(' - ')}</Badge></div>
                                <div className="space-y-2 pl-7">
                                  {groupedTasks.morning.map((task) => (
                                    <TaskItem key={task.task_id} task={task} parkId={park.park_id} personnelId={personnelId} date={selectedDate} onComplete={handleTaskComplete} isCompleting={completingTask === `${park.park_id}-${task.task_id}`} weatherCondition={park.manual_weather} onMediaCapture={(t) => handleMediaCapture(park.park_id, t)} readOnly={isOnLeave} />
                                  ))}
                                </div>
                              </div>
                            )}
                            {groupedTasks.noon.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-3"><Cloud className="h-5 w-5 text-orange-500" /><h4 className="font-medium">Öğle Mıntıkası</h4><Badge variant="outline" className="text-xs">{groupedTasks.noon[0]?.expected_time_range?.join(' - ')}</Badge></div>
                                <div className="space-y-2 pl-7">
                                  {groupedTasks.noon.map((task) => (
                                    <TaskItem key={task.task_id} task={task} parkId={park.park_id} personnelId={personnelId} date={selectedDate} onComplete={handleTaskComplete} isCompleting={completingTask === `${park.park_id}-${task.task_id}`} weatherCondition={park.manual_weather} onMediaCapture={(t) => handleMediaCapture(park.park_id, t)} readOnly={isOnLeave} />
                                  ))}
                                </div>
                              </div>
                            )}
                            {groupedTasks.evening.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-3"><Moon className="h-5 w-5 text-blue-500" /><h4 className="font-medium">Akşam Mıntıkası</h4><Badge variant="outline" className="text-xs">{groupedTasks.evening[0]?.expected_time_range?.join(' - ')}</Badge></div>
                                <div className="space-y-2 pl-7">
                                  {groupedTasks.evening.map((task) => (
                                    <TaskItem key={task.task_id} task={task} parkId={park.park_id} personnelId={personnelId} date={selectedDate} onComplete={handleTaskComplete} isCompleting={completingTask === `${park.park_id}-${task.task_id}`} weatherCondition={park.manual_weather} onMediaCapture={(t) => handleMediaCapture(park.park_id, t)} readOnly={isOnLeave} />
                                  ))}
                                </div>
                              </div>
                            )}
                            {groupedTasks.general.length > 0 && (
                              <div>
                                <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="h-5 w-5 text-gray-500" /><h4 className="font-medium">Genel Görevler</h4></div>
                                <div className="space-y-2 pl-7">
                                  {groupedTasks.general.map((task) => (
                                    <TaskItem key={task.task_id} task={task} parkId={park.park_id} personnelId={personnelId} date={selectedDate} onComplete={handleTaskComplete} isCompleting={completingTask === `${park.park_id}-${task.task_id}`} weatherCondition={park.manual_weather} onMediaCapture={(t) => handleMediaCapture(park.park_id, t)} readOnly={isOnLeave} />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">Vekalet Park Bulunamadı</h3>
                  <p className="text-muted-foreground">
                    Bu tarih için vekaleten baktığınız park bulunmuyor.
                  </p>
                </CardContent>
              </Card>
            )}

          </SubTabsContent>
        )}

        {/* Görsel Bakış (Amirler için) */}
        {canAccessVisualOverview && (
          <SubTabsContent value="visual-overview" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5 text-indigo-600" />
                  Görsel Bakış
                </CardTitle>
                <CardDescription>
                  {formatDateTR(selectedDate)} tarihinde yüklenen görev görselleri.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Park, personel veya görev ara..."
                      value={visualSearch}
                      onChange={(e) => setVisualSearch(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Park filtresi</label>
                    <SearchableSelect
                      value={visualParkFilter}
                      onChange={setVisualParkFilter}
                      allLabel="Tüm Parklar"
                      searchPlaceholder="Park adı ara..."
                      emptyText={
                        visualParkOptions.length === 0
                          ? "Bu tarihte park kaydı yok."
                          : "Sonuç bulunamadı."
                      }
                      className="h-9 text-sm w-full"
                      options={visualParkOptions.map((name) => ({ id: name, name }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[11px] text-muted-foreground">Personel filtresi</label>
                    <SearchableSelect
                      value={visualPersonnelFilter}
                      onChange={setVisualPersonnelFilter}
                      allLabel="Tüm Personel"
                      searchPlaceholder="Personel adı ara..."
                      emptyText={
                        visualPersonnelOptions.length === 0
                          ? "Bu tarihte personel kaydı yok."
                          : "Sonuç bulunamadı."
                      }
                      className="h-9 text-sm w-full"
                      options={visualPersonnelOptions.map((name) => ({ id: name, name }))}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={applyVisualFilters} className="h-9">
                    <Search className="h-4 w-4 mr-1" />
                    Ara
                  </Button>
                  <Button variant="outline" onClick={clearVisualFilters} className="h-9">
                    Filtreyi Temizle
                  </Button>
                </div>
                {(visualParkQuery && filteredVisualParkOptions.length === 0) && (
                  <p className="text-[11px] text-muted-foreground">Park aramasına uygun seçenek bulunamadı.</p>
                )}
                {(visualPersonnelQuery && filteredVisualPersonnelOptions.length === 0) && (
                  <p className="text-[11px] text-muted-foreground">Personel aramasına uygun seçenek bulunamadı.</p>
                )}
                {(visualParkOptions.length === 0 || visualPersonnelOptions.length === 0) && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Bu tarihte dropdown seçenekleri için yeterli görev kaydı bulunamadı. Tarihi değiştirip tekrar deneyin.
                  </p>
                )}

                <div className="text-xs text-muted-foreground">
                  Toplam {filteredVisualMediaRows.length} kanıt kaydı listeleniyor.
                </div>

                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
                  <p className="text-sm font-semibold text-indigo-900 mb-2">Örnekler</p>
                  <div className="space-y-2 text-xs text-indigo-900">
                    {filteredVisualExampleRows.map((example, idx) => (
                      <button
                        key={example.id}
                        type="button"
                        onClick={() => applyVisualExample(example.search_key, `Örnek ${idx + 1} filtresi uygulandı.`)}
                        className="w-full text-left rounded-md bg-white/80 border border-indigo-100 px-2 py-1.5 hover:bg-white transition-colors cursor-pointer"
                      >
                        <span className="font-medium">Örnek {idx + 1}:</span> {example.park_name} • {example.personnel_name} • {example.task_name} •
                        <span className="ml-1">{example.on_time ? "Zamanında" : "Geç"}</span> • <span className="ml-1">{example.media_type}</span>
                      </button>
                    ))}
                    {filteredVisualExampleRows.length === 0 && (
                      <p className="text-[11px] text-indigo-700">Uygulanan filtreye uygun örnek görünümü bulunmuyor.</p>
                    )}
                    <p className="text-[11px] text-indigo-700">
                      İpucu: Satıra göre park/personel/görev ve zamanında-geç durumunu kontrol edin. Görselin üstüne tıklayarak büyük açabilirsiniz.
                    </p>
                  </div>
                </div>

                {filteredVisualMediaRows.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground border rounded-lg bg-slate-50">
                    Bu kriterlere uygun görsel bulunamadı.
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                    {filteredVisualMediaRows.map((row) => (
                      <div key={row.id} className="border rounded-lg p-3 bg-white">
                        <div className="mb-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                          {`${row.park_name} ${row.personnel_name} ${row.task_name} ${row.on_time === false ? 'geç' : 'zamanında'} ${row.media_type || 'media'}`}
                        </div>
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
                          <div>
                            <p className="font-semibold text-gray-900">{row.park_name}</p>
                            <p className="text-sm text-gray-600">
                              {row.personnel_name} • {row.task_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {row.on_time === false ? (
                              <Badge variant="outline" className="border-amber-500 text-amber-700">Geç</Badge>
                            ) : (
                              <Badge className="bg-green-600">Zamanında</Badge>
                            )}
                            <Badge variant="outline">{row.media_type || "media"}</Badge>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          Yükleme zamanı: {row.completed_at ? new Date(row.completed_at).toLocaleString("tr-TR") : "-"}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {row.media_urls.map((url, idx) => (
                            <a
                              key={`${row.id}_media_${idx}`}
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="block border rounded-md overflow-hidden hover:opacity-90 transition-opacity bg-slate-50"
                            >
                              {isVideoMedia(url, row.media_type) ? (
                                <video src={url} controls className="w-full h-24 object-cover bg-black" />
                              ) : (
                                <img src={url} alt={`${row.task_name} ${idx + 1}`} className="w-full h-24 object-cover" />
                              )}
                            </a>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </SubTabsContent>
        )}

        {/* Ayarlar (Sadece Müdür/Müdür Yardımcısı) */}
        {canAccessSettings && (
          <SubTabsContent value="settings" className="space-y-4 mt-4">
            <div className="grid gap-4 md:grid-cols-4">
              {/* Park Seçimi (Sol taraf) */}
              <div className="md:col-span-1">
                <Card className="h-fit">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Park Seçin</CardTitle>
                    <CardDescription className="text-xs">Ayarlamak istediğiniz parkı seçin</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Arama Kutusu */}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Park ara..."
                        value={settingsParkSearch}
                        onChange={(e) => setSettingsParkSearch(e.target.value)}
                        className="pl-8 h-9 text-sm"
                      />
                    </div>
                    
                    {/* Bölge Filtresi */}
                    {filteredRegionsForSettings.length > 0 && (
                      <SearchableSelect
                        value={settingsRegionFilter}
                        onChange={setSettingsRegionFilter}
                        className="h-9 text-sm"
                        allLabel={isMudurYardimcisi ? 'Bölgelerim' : 'Tüm Bölgeler'}
                        allValue="all"
                        searchPlaceholder="Bölge ara..."
                        options={[
                          ...(!isMudurYardimcisi ? [{ id: 'unassigned', name: 'Bölge Atanmamış' }] : []),
                          ...filteredRegionsForSettings.map((region) => ({ id: region.id, name: region.name })),
                        ]}
                      />
                    )}
                    
                    {/* Müdür Yardımcısı için bilgi */}
                    {isMudurYardimcisi && (
                      <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded text-center">
                        📍 Sadece size bağlı bölgeler gösteriliyor
                      </div>
                    )}
                    
                    {/* Filtrelenmiş Park Sayısı */}
                    {(() => {
                      const baseParks = filteredParksForSettings; // Müdür Yardımcısı için zaten filtrelenmiş
                      const filteredParks = baseParks.filter(park => {
                        const matchesSearch = !normalizeSearchText(settingsParkSearch) ||
                          normalizeSearchText(park.name).includes(normalizeSearchText(settingsParkSearch));
                        const matchesRegion = settingsRegionFilter === "all" || 
                          (settingsRegionFilter === "unassigned" && !park.region_id) ||
                          park.region_id === settingsRegionFilter;
                        return matchesSearch && matchesRegion;
                      });
                      
                      return (
                        <>
                          <div className="text-xs text-muted-foreground text-center py-1 border-b">
                            {filteredParks.length} / {filteredParksForSettings.length} park
                          </div>
                          
                          {/* Park Listesi - Scroll Alanı */}
                          <div className="max-h-[400px] overflow-y-auto space-y-1.5 pr-1">
                            {filteredParks.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                {settingsParkSearch || settingsRegionFilter !== "all" 
                                  ? "Eşleşen park bulunamadı" 
                                  : "Park bulunamadı"}
                              </p>
                            ) : (
                              [...filteredParks].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map((park) => (
                                <Button
                                  key={park.id}
                                  variant={selectedParkForSettings === park.id ? "default" : "outline"}
                                  className="w-full justify-start h-auto py-2 px-3 text-left"
                                  onClick={() => handleParkSelect(park.id)}
                                >
                                  <div className="flex flex-col items-start gap-0.5 w-full overflow-hidden">
                                    <span className="text-sm font-medium truncate w-full">{park.name}</span>
                                    {park.region_id && regions.find(r => r.id === park.region_id) && (
                                      <span className="text-xs text-muted-foreground truncate w-full">
                                        📍 {regions.find(r => r.id === park.region_id)?.name}
                                      </span>
                                    )}
                                  </div>
                                </Button>
                              ))
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </div>

              {/* Park Ayarları (Sağ taraf) */}
              <div className="md:col-span-3">
                {!selectedParkForSettings ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <h3 className="text-lg font-semibold mb-2">Park Ayarları</h3>
                      <p className="text-muted-foreground">
                        Lütfen sol taraftan bir park seçin
                      </p>
                    </CardContent>
                  </Card>
                ) : !parkConfig ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <p className="text-muted-foreground">Yükleniyor...</p>
                    </CardContent>
                  </Card>
                ) : (
                  <fieldset disabled={isViewOnlySettings} className="space-y-4">
                    {/* Bölge Mühendisi veya Formen için uyarı */}
                    {isViewOnlySettings && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        <strong>⚠️ Sadece Görüntüleme:</strong> {isFormen ? 'Formen' : 'Bölge Mühendisi'} olarak ayarları sadece görüntüleyebilirsiniz, değişiklik yapamazsınız.
                      </div>
                    )}
                    {/* Saat Dilimleri */}
                    <Card className={isViewOnlySettings ? 'opacity-60' : ''}>
                      <CardHeader>
                        <CardTitle>⏰ Saat Dilimleri</CardTitle>
                        <CardDescription>Her mıntıka için zaman aralığı belirleyin</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                          <div>
                            <label className="text-sm font-medium">Sabah Mıntıkası</label>
                            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 mt-2">
                              <input
                                type="time"
                                value={parkConfig.time_slots.morning.start}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    morning: { ...parkConfig.time_slots.morning, start: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                              <span>-</span>
                              <input
                                type="time"
                                value={parkConfig.time_slots.morning.end}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    morning: { ...parkConfig.time_slots.morning, end: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-sm font-medium">Öğle Mıntıkası</label>
                            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 mt-2">
                              <input
                                type="time"
                                value={parkConfig.time_slots.noon.start}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    noon: { ...parkConfig.time_slots.noon, start: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                              <span>-</span>
                              <input
                                type="time"
                                value={parkConfig.time_slots.noon.end}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    noon: { ...parkConfig.time_slots.noon, end: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-sm font-medium">Akşam Mıntıkası</label>
                            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 mt-2">
                              <input
                                type="time"
                                value={parkConfig.time_slots.evening.start}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    evening: { ...parkConfig.time_slots.evening, start: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                              <span>-</span>
                              <input
                                type="time"
                                value={parkConfig.time_slots.evening.end}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  time_slots: {
                                    ...parkConfig.time_slots,
                                    evening: { ...parkConfig.time_slots.evening, end: e.target.value }
                                  }
                                })}
                                className="w-full min-w-0 px-3 py-2 border rounded-md text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Park Özellikleri */}
                    <Card className={isViewOnlySettings ? 'opacity-60' : ''}>
                      <CardHeader>
                        <CardTitle>🏞️ Park Özellikleri ve Medya Gereksinimleri</CardTitle>
                        <CardDescription>Bu parkta hangi özellikler var? Kontrol için medya (fotoğraf/video) gerekli mi?</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 md:grid-cols-2">
                          {[
                            { key: 'playground', label: 'Çocuk Oyun Grubu', icon: '🎠', hint: 'Oyun alanı güvenlik kontrolü' },
                            { key: 'sports_field', label: 'Spor Sahası', icon: '⚽', hint: 'Saha durumu kontrolü' },
                            { key: 'sports_equipment', label: 'Spor Aletleri', icon: '🏋️', hint: 'Alet güvenlik kontrolü' },
                            { key: 'walking_path', label: 'Yürüyüş Yolu', icon: '🚶', hint: 'Yol durumu kontrolü' },
                            { key: 'pool', label: 'Havuz', icon: '🏊', hint: 'Havuz temizlik kontrolü' },
                            { key: 'restroom', label: 'WC/Tuvalet', icon: '🚻', hint: 'Tuvalet temizlik kontrolü' },
                            { key: 'other_feature', label: 'Diğer', icon: '📋', hint: 'Diğer özellik kontrolü' }
                          ].map((feature) => (
                            <div key={feature.key} className="flex flex-col gap-2 p-3 border rounded-lg hover:bg-slate-50">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={parkConfig.features[feature.key]?.enabled || false}
                                  onChange={(e) => setParkConfig({
                                    ...parkConfig,
                                    features: {
                                      ...parkConfig.features,
                                      [feature.key]: {
                                        ...parkConfig.features[feature.key],
                                        enabled: e.target.checked
                                      }
                                    }
                                  })}
                                  className="w-4 h-4 accent-green-600"
                                />
                                <span className="text-lg">{feature.icon}</span>
                                <div>
                                  <label className="text-sm font-medium">{feature.label}</label>
                                  <p className="text-xs text-muted-foreground">{feature.hint}</p>
                                </div>
                              </div>
                              {parkConfig.features[feature.key]?.enabled && (
                                <div className="flex flex-wrap gap-1 ml-7">
                                  {/* Medya Tipi Seçimi - Park Özellikleri için */}
                                  {[
                                    { value: 'none', label: 'Medya Yok', bg: 'bg-gray-100', text: 'text-gray-600', icon: '❌' },
                                    { value: 'photo', label: 'Fotoğraf', bg: 'bg-amber-100', text: 'text-amber-700', icon: '📷' },
                                    { value: 'video', label: 'Video', bg: 'bg-blue-100', text: 'text-blue-700', icon: '🎥' },
                                    { value: 'both', label: 'Her İkisi', bg: 'bg-purple-100', text: 'text-purple-700', icon: '📸' }
                                  ].map((mediaOption) => {
                                    const currentMediaType = parkConfig.features[feature.key]?.media_type || 
                                      (parkConfig.features[feature.key]?.photo_required ? 'photo' : 'none');
                                    const isSelected = currentMediaType === mediaOption.value;
                                    return (
                                      <button
                                        key={mediaOption.value}
                                        type="button"
                                        onClick={() => setParkConfig({
                                          ...parkConfig,
                                          features: {
                                            ...parkConfig.features,
                                            [feature.key]: {
                                              ...parkConfig.features[feature.key],
                                              media_type: mediaOption.value,
                                              photo_required: mediaOption.value === 'photo' || mediaOption.value === 'both'
                                            }
                                          }
                                        })}
                                        className={`px-2 py-1 text-xs rounded-md border transition-all ${
                                          isSelected 
                                            ? `${mediaOption.bg} ${mediaOption.text} border-current font-medium` 
                                            : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                        }`}
                                      >
                                        <span className="mr-1">{mediaOption.icon}</span>
                                        {mediaOption.label}
                                      </button>
                                    );
                                  })}
                                  <span className="text-xs text-muted-foreground ml-2 self-center">(max 3)</span>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {/* Diğer özellik için açıklama alanı */}
                          {parkConfig.features.other_feature?.enabled && (
                            <div className="md:col-span-2 p-3 border rounded-lg bg-slate-50">
                              <label className="text-sm font-medium block mb-2">Diğer Özellik Açıklaması</label>
                              <Input
                                placeholder="Örn: Piknik alanı, Bisiklet parkı..."
                                value={parkConfig.features.other_feature?.description || ''}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  features: {
                                    ...parkConfig.features,
                                    other_feature: {
                                      ...parkConfig.features.other_feature,
                                      description: e.target.value
                                    }
                                  }
                                })}
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Zorunlu Görevler ve Medya Tipi Ayarları */}
                    <Card className={isViewOnlySettings ? 'opacity-60' : ''}>
                      <CardHeader>
                        <CardTitle>📸 Zorunlu Görevler ve Medya Gereksinimleri</CardTitle>
                        <CardDescription>Günlük yapılacak görevler ve medya (fotoğraf/video) gerekliliği</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-3 md:grid-cols-2">
                          {[
                            { key: 'morning_trash', label: 'Sabah Mıntıkası - Çöp Kovaları', icon: '☀️' },
                            { key: 'noon_trash', label: 'Öğle Mıntıkası - Çöp Kovaları', icon: '🌤️' },
                            { key: 'evening_trash', label: 'Akşam Mıntıkası - Çöp Kovaları', icon: '🌙' },
                            { key: 'floor_cleaning', label: 'Sert Zemin Temizliği', icon: '🧹' },
                            { key: 'leaf_cleaning', label: 'Yaprak/Çöp Temizliği', icon: '🍂' },
                            { key: 'watering', label: 'Sulama', icon: '💧' },
                            { key: 'other_task', label: 'Diğer Görev', icon: '📋' }
                          ].map((task) => (
                            <div key={task.key} className="flex flex-col gap-2 p-3 border rounded-lg hover:bg-slate-50">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={parkConfig.mandatory_tasks?.[task.key]?.enabled !== false}
                                  onChange={(e) => setParkConfig({
                                    ...parkConfig,
                                    mandatory_tasks: {
                                      ...parkConfig.mandatory_tasks,
                                      [task.key]: {
                                        ...parkConfig.mandatory_tasks?.[task.key],
                                        enabled: e.target.checked
                                      }
                                    }
                                  })}
                                  className="w-4 h-4 accent-green-600"
                                />
                                <span className="text-lg">{task.icon}</span>
                                <label className="text-sm font-medium">{task.label}</label>
                              </div>
                              {parkConfig.mandatory_tasks?.[task.key]?.enabled !== false && (
                                <div className="flex flex-wrap gap-1 ml-7">
                                  {/* Medya Tipi Seçimi */}
                                  {[
                                    { value: 'none', label: 'Medya Yok', bg: 'bg-gray-100', text: 'text-gray-600', icon: '❌' },
                                    { value: 'photo', label: 'Fotoğraf', bg: 'bg-amber-100', text: 'text-amber-700', icon: '📷' },
                                    { value: 'video', label: 'Video', bg: 'bg-blue-100', text: 'text-blue-700', icon: '🎥' },
                                    { value: 'both', label: 'Her İkisi', bg: 'bg-purple-100', text: 'text-purple-700', icon: '📸' }
                                  ].map((mediaOption) => {
                                    const currentMediaType = parkConfig.mandatory_tasks?.[task.key]?.media_type || 
                                      (parkConfig.mandatory_tasks?.[task.key]?.photo_required ? 'photo' : 'none');
                                    const isSelected = currentMediaType === mediaOption.value;
                                    return (
                                      <button
                                        key={mediaOption.value}
                                        type="button"
                                        onClick={() => setParkConfig({
                                          ...parkConfig,
                                          mandatory_tasks: {
                                            ...parkConfig.mandatory_tasks,
                                            [task.key]: {
                                              ...parkConfig.mandatory_tasks?.[task.key],
                                              media_type: mediaOption.value,
                                              photo_required: mediaOption.value === 'photo' || mediaOption.value === 'both'
                                            }
                                          },
                                          mandatory_photos: {
                                            ...parkConfig.mandatory_photos,
                                            [task.key]: mediaOption.value === 'photo' || mediaOption.value === 'both'
                                          }
                                        })}
                                        className={`px-2 py-1 text-xs rounded-md border transition-all ${
                                          isSelected 
                                            ? `${mediaOption.bg} ${mediaOption.text} border-current font-medium` 
                                            : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                                        }`}
                                      >
                                        <span className="mr-1">{mediaOption.icon}</span>
                                        {mediaOption.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {/* Diğer görev için açıklama alanı */}
                          {parkConfig.mandatory_tasks?.other_task?.enabled && (
                            <div className="md:col-span-2 p-3 border rounded-lg bg-slate-50">
                              <label className="text-sm font-medium block mb-2">Diğer Görev Açıklaması</label>
                              <Input
                                placeholder="Örn: Bankları sil, Çiçekleri sula..."
                                value={parkConfig.mandatory_tasks?.other_task?.description || ''}
                                onChange={(e) => setParkConfig({
                                  ...parkConfig,
                                  mandatory_tasks: {
                                    ...parkConfig.mandatory_tasks,
                                    other_task: {
                                      ...parkConfig.mandatory_tasks?.other_task,
                                      description: e.target.value
                                    }
                                  }
                                })}
                              />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Hafta Sonu Mesai Ayarları */}
                    <Card className={isViewOnlySettings ? 'opacity-60' : ''}>
                      <CardHeader>
                        <CardTitle>📅 Hafta Sonu Mesai</CardTitle>
                        <CardDescription>Bu park için hafta sonu çalışma düzenlemesi</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <label className="text-sm font-medium">Hafta Sonu Mesai Aktif</label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Bu park için hafta sonu görev ataması yapılsın mı?
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={parkConfig.weekend_enabled || false}
                            onChange={(e) => setParkConfig({
                              ...parkConfig,
                              weekend_enabled: e.target.checked
                            })}
                            className="w-5 h-5 accent-green-600"
                          />
                        </div>
                        
                        {parkConfig.weekend_enabled && (
                          <div className="p-4 border rounded-lg bg-blue-50">
                            <label className="text-sm font-medium block mb-2">
                              👷 Hafta Sonu Mesaiye Kalacak Personel
                            </label>
                            <p className="text-xs text-muted-foreground mb-3">
                              Bu parkın kendi görevlisi dışında başka personel de seçilebilir
                            </p>
                            <SearchableSelect
                              value={parkConfig.weekend_personnel_id || "default"}
                              onChange={(val) => setParkConfig({
                                ...parkConfig,
                                weekend_personnel_id: val === "default" ? null : val
                              })}
                              className="bg-white"
                              allLabel="🏠 Parkın kendi görevlisi"
                              allValue="default"
                              searchPlaceholder="Personel ara..."
                              options={personnel
                                .filter(p => p.role === 'park_gorevlisi')
                                .map((p) => ({ id: p.id, name: `👷 ${p.name}` }))}
                            />
                            
                            {/* Birden fazla personel seçimi */}
                            <div className="mt-3">
                              <label className="text-xs text-muted-foreground">
                                Veya birden fazla personel için virgülle ayırarak ID girin
                              </label>
                              <Input
                                placeholder="Birden fazla personel seçilecekse..."
                                value={parkConfig.weekend_personnel_ids?.join(', ') || ''}
                                onChange={(e) => {
                                  const ids = e.target.value.split(',').map(id => id.trim()).filter(id => id);
                                  setParkConfig({
                                    ...parkConfig,
                                    weekend_personnel_ids: ids.length > 0 ? ids : undefined
                                  });
                                }}
                                className="mt-1 bg-white"
                              />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Hava Durumu Ayarları */}
                    <Card className={isViewOnlySettings ? 'opacity-60' : ''}>
                      <CardHeader>
                        <CardTitle>🌤️ Hava Durumu Ayarları</CardTitle>
                        <CardDescription>Manuel hava durumu ayarı ve görev etkileri</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <label className="text-sm font-medium">Otomatik Hava Durumu</label>
                            <p className="text-xs text-muted-foreground mt-1">
                              Hava durumuna göre görevler otomatik ayarlansın mı?
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={parkConfig.weather_integration_enabled !== false}
                            onChange={(e) => setParkConfig({
                              ...parkConfig,
                              weather_integration_enabled: e.target.checked
                            })}
                            className="w-5 h-5 accent-green-600"
                          />
                        </div>
                        
                        {/* Manuel Hava Durumu Seçimi */}
                        <div className="p-4 border rounded-lg bg-amber-50">
                          <label className="text-sm font-medium block mb-2">
                            📍 Bu Park İçin Manuel Hava Durumu
                          </label>
                          <p className="text-xs text-muted-foreground mb-3">
                            Otomatik sistem yerine manuel ayar yapabilirsiniz
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {[
                              { key: 'normal', label: 'Normal', icon: '☀️' },
                              { key: 'rainy', label: 'Yağmurlu', icon: '🌧️' },
                              { key: 'snowy', label: 'Karlı', icon: '❄️' },
                              { key: 'hot', label: 'Aşırı Sıcak', icon: '🔥' },
                              { key: 'stormy', label: 'Fırtınalı', icon: '⛈️' },
                              { key: 'foggy', label: 'Sisli', icon: '🌫️' },
                              { key: 'windy', label: 'Rüzgarlı', icon: '💨' },
                              { key: 'none', label: 'Ayar Yok', icon: '➖' }
                            ].map((weather) => (
                              <button
                                key={weather.key}
                                type="button"
                                onClick={() => setParkConfig({
                                  ...parkConfig,
                                  manual_weather: weather.key === 'none' ? null : weather.key
                                })}
                                className={`p-3 rounded-lg border-2 text-center transition-all ${
                                  parkConfig.manual_weather === weather.key || 
                                  (weather.key === 'none' && !parkConfig.manual_weather)
                                    ? 'border-amber-500 bg-amber-100' 
                                    : 'border-gray-200 bg-white hover:border-amber-300'
                                }`}
                              >
                                <span className="text-2xl block">{weather.icon}</span>
                                <span className="text-xs mt-1 block">{weather.label}</span>
                              </button>
                            ))}
                          </div>
                          
                          {parkConfig.manual_weather && parkConfig.manual_weather !== 'normal' && (
                            <div className="mt-3 p-3 bg-white rounded border">
                              <p className="text-sm">
                                <strong>⚠️ Etkilenen Görevler:</strong>
                              </p>
                              <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                                {parkConfig.manual_weather === 'rainy' && (
                                  <>
                                    <li>• Sulama görevi atlanacak</li>
                                    <li>• Yaprak temizliği zorlaşabilir</li>
                                  </>
                                )}
                                {parkConfig.manual_weather === 'snowy' && (
                                  <>
                                    <li>• Sulama görevi atlanacak</li>
                                    <li>• Kar temizliği gerekli olabilir</li>
                                  </>
                                )}
                                {parkConfig.manual_weather === 'hot' && (
                                  <>
                                    <li>• Sulama daha sık yapılabilir</li>
                                    <li>• Öğle mesai dikkatli planlanmalı</li>
                                  </>
                                )}
                                {parkConfig.manual_weather === 'stormy' && (
                                  <>
                                    <li>• Dış mekan görevleri tehlikeli</li>
                                    <li>• Görevler ertelenebilir</li>
                                  </>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Kaydet Butonu - Bölge Mühendisi göremez */}
                    {canEditSettings && (
                      <div className="flex justify-end">
                        <Button 
                          onClick={saveParkConfig} 
                          disabled={savingConfig}
                          size="lg"
                        >
                          {savingConfig ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
                        </Button>
                      </div>
                    )}
                  </fieldset>
                )}
              </div>
            </div>
          </SubTabsContent>
        )}
      </Tabs>
      
      {/* Detay Modal - Sürüklenebilir */}
      <DraggableModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={detailModalTitle}
        subtitle={`${detailModalData?.length || 0} kayıt bulundu`}
      >
        {detailModalData?.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Park</th>
                  <th className="text-left p-3 text-sm font-medium">Personel</th>
                  {detailModalType === "skipped" && (
                    <th className="text-center p-3 text-sm font-medium">Durum</th>
                  )}
                  {(detailModalType === "late" || detailModalType === "late_all") && (
                    <>
                      {detailModalType === "late_all" && (
                        <th className="text-left p-3 text-sm font-medium">Görev</th>
                      )}
                      <th className="text-center p-3 text-sm font-medium">Beklenen</th>
                      <th className="text-center p-3 text-sm font-medium">Yapılan</th>
                    </>
                  )}
                  {detailModalType === "on_time" && (
                    <>
                      <th className="text-left p-3 text-sm font-medium">Görev</th>
                      <th className="text-center p-3 text-sm font-medium">Tamamlanma</th>
                    </>
                  )}
                  {(detailModalType === "in_progress" || detailModalType === "pending") && (
                    <th className="text-center p-3 text-sm font-medium">Görev</th>
                  )}
                  {detailModalType === "completed" && (
                    <th className="text-center p-3 text-sm font-medium">Zamanında</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {detailModalData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="p-3 text-sm">{item.park_name}</td>
                    <td className="p-3 text-sm">
                      {(() => {
                        const byName = item.completed_by_name;
                        const byNames = item.completed_by_names;
                        const hasVekaleten = byName || (Array.isArray(byNames) && byNames.length > 0);
                        const vekaletenLabel = byName ? byName : (byNames && byNames.length) ? byNames.join(', ') : null;
                        if (hasVekaleten && vekaletenLabel) {
                          return (
                            <span>
                              <span className="text-muted-foreground">{item.personnel_name}</span>
                              <span className="text-gray-400 mx-1">·</span>
                              <span className="text-amber-600 font-medium">{vekaletenLabel} (vekaleten)</span>
                            </span>
                          );
                        }
                        return item.personnel_name;
                      })()}
                    </td>
                    {detailModalType === "skipped" && (
                      <td className="p-3 text-center">
                        <Badge variant="secondary">{item.status === "pending" ? "Bekliyor" : "Devam Ediyor"}</Badge>
                      </td>
                    )}
                    {(detailModalType === "late" || detailModalType === "late_all") && (
                      <>
                        {detailModalType === "late_all" && (
                          <td className="p-3 text-sm">{item.task_name}</td>
                        )}
                        <td className="p-3 text-center text-sm text-gray-500">{item.expected_time}</td>
                        <td className="p-3 text-center text-sm">
                          {item.completed_at ? new Date(item.completed_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                      </>
                    )}
                    {detailModalType === "on_time" && (
                      <>
                        <td className="p-3 text-sm">{item.task_name}</td>
                        <td className="p-3 text-center text-sm">
                          {item.completed_at ? new Date(item.completed_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                      </>
                    )}
                    {detailModalType === "in_progress" && (
                      <td className="p-3 text-center">
                        <Badge className="bg-blue-500">{item.completed_tasks}/{item.total_tasks}</Badge>
                      </td>
                    )}
                    {detailModalType === "pending" && (
                      <td className="p-3 text-center">
                        <Badge variant="secondary">{item.total_tasks} görev</Badge>
                      </td>
                    )}
                    {detailModalType === "completed" && (
                      <td className="p-3 text-center">
                        <Badge className="bg-green-500">%{item.on_time_rate}</Badge>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">Kayıt bulunamadı</p>
        )}
      </DraggableModal>

      {/* Park Görev Detayları Modal - Sürüklenebilir */}
      <DraggableModal
        isOpen={parkTaskDetailOpen}
        onClose={() => setParkTaskDetailOpen(false)}
        title={
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-600" />
            {parkTaskDetailData?.park_name || 'Park Görev Detayları'}
          </span>
        }
        subtitle={
          <span>
            {formatDateTR(selectedDate)} tarihli görev durumu
            {openDetailCoverage?.substitute_personnel_name && (
              <span className="ml-2">• Bugün vekalet: <strong>{openDetailCoverage.substitute_personnel_name}</strong></span>
            )}
          </span>
        }
      >
        {loadingParkTasks ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Görevler yükleniyor...</span>
          </div>
        ) : parkTaskDetailData?.error ? (
          <div className="text-center py-8 text-gray-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
            <p>Görev detayları yüklenemedi</p>
          </div>
        ) : (
          <>
            {/* Görevli & Hiyerarşi — park görevlisinin bağlı olduğu Formen → Bölge Mühendisi → Müdür Yardımcısı zinciri */}
            {parkTaskDetailData?.personnel_id && (() => {
              const person = personnel.find(p => p.id === parkTaskDetailData.personnel_id);
              if (!person) return null;

              let formen = null;
              let bolgeMuhendisi = null;
              let mudurYardimcisi = null;

              if (person.formen_id) {
                formen = personnel.find(p => p.id === person.formen_id);
              } else if (person.parent_id) {
                const parent = personnel.find(p => p.id === person.parent_id);
                if (parent?.role === 'formen') formen = parent;
              }

              if (person.bolge_muhendisi_id) {
                bolgeMuhendisi = personnel.find(p => p.id === person.bolge_muhendisi_id);
              } else if (formen?.bolge_muhendisi_id) {
                bolgeMuhendisi = personnel.find(p => p.id === formen.bolge_muhendisi_id);
              } else if (formen?.parent_id) {
                const formenParent = personnel.find(p => p.id === formen.parent_id);
                if (formenParent?.role === 'bolge_muhendisi') bolgeMuhendisi = formenParent;
              } else if (person.parent_id && !formen) {
                const parent = personnel.find(p => p.id === person.parent_id);
                if (parent?.role === 'bolge_muhendisi') bolgeMuhendisi = parent;
              }

              if (bolgeMuhendisi?.mudur_yardimcisi_id) {
                mudurYardimcisi = personnel.find(p => p.id === bolgeMuhendisi.mudur_yardimcisi_id);
              } else if (bolgeMuhendisi?.parent_id) {
                const bmParent = personnel.find(p => p.id === bolgeMuhendisi.parent_id);
                if (bmParent?.role === 'mudur_yardimcisi') mudurYardimcisi = bmParent;
              }

              const roleBadge = {
                park_gorevlisi: { label: 'Park Görevlisi', className: 'bg-green-500' },
                'çim_ekibi': { label: 'Çim Ekibi', className: 'bg-emerald-500' },
                formen: { label: 'Formen', className: 'bg-yellow-500' },
                bolge_muhendisi: { label: 'Bölge Mühendisi', className: 'bg-blue-500' },
                mudur_yardimcisi: { label: 'Müdür Yardımcısı', className: 'bg-purple-500' },
              };
              const personBadge = roleBadge[person.role] || { label: person.role || 'Görevli', className: 'bg-gray-500' };

              return (
                <Card className="bg-slate-50 border-slate-200 mb-3">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-slate-600" />
                      <Badge className={`${personBadge.className} text-white text-xs`}>{personBadge.label}</Badge>
                      <span className="font-semibold text-gray-800">{person.name}</span>
                    </div>
                    {formen && (
                      <div className="flex items-center gap-2 text-sm pl-6 border-l-2 border-yellow-200 ml-2">
                        <Badge className="bg-yellow-500 text-white text-xs">Formen</Badge>
                        <span className="text-gray-700">{formen.name}</span>
                      </div>
                    )}
                    {bolgeMuhendisi && (
                      <div className="flex items-center gap-2 text-sm pl-6 border-l-2 border-blue-200 ml-2">
                        <Badge className="bg-blue-500 text-white text-xs">Bölge Mühendisi</Badge>
                        <span className="text-gray-700">{bolgeMuhendisi.name}</span>
                      </div>
                    )}
                    {mudurYardimcisi && (
                      <div className="flex items-center gap-2 text-sm pl-6 border-l-2 border-purple-200 ml-2">
                        <Badge className="bg-purple-500 text-white text-xs">Müdür Yardımcısı</Badge>
                        <span className="text-gray-700">{mudurYardimcisi.name}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Özet Kartları */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {parkTaskDetailData?.completed_tasks || 0}
                  </div>
                  <div className="text-xs text-green-600">Tamamlanan</div>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">
                    {(parkTaskDetailData?.total_tasks || 0) - (parkTaskDetailData?.completed_tasks || 0)}
                  </div>
                  <div className="text-xs text-amber-600">Bekleyen</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">
                        %{parkTaskDetailData?.completion_rate || 0}
                      </div>
                      <div className="text-xs text-blue-600">Tamamlanma</div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Görev Listesi */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Görev Listesi</h4>
                  
                  {parkTaskDetailData?.tasks?.length > 0 ? (
                    <div className="space-y-2">
                      {parkTaskDetailData.tasks.map((task, idx) => (
                        <div 
                          key={task.task_id || idx}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            task.completed 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {task.completed ? (
                              <CheckCircle2 className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-gray-400" />
                            )}
                            <div>
                              <p className={`font-medium ${task.completed ? 'text-green-800' : 'text-gray-700'}`}>
                                {task.task_name || task.name || task.task_id}
                              </p>
                              {task.expected_time_range && task.expected_time_range.length === 2 && (
                                <p className="text-xs text-muted-foreground">
                                  Beklenen: {task.expected_time_range[0]} - {task.expected_time_range[1]}
                                </p>
                              )}
                              {task.time_slot && (
                                <p className="text-xs text-muted-foreground capitalize">
                                  {task.time_slot === 'morning' ? '🌅 Sabah' : 
                                   task.time_slot === 'noon' ? '☀️ Öğle' : 
                                   task.time_slot === 'evening' ? '🌆 Akşam' : 
                                   task.time_slot === 'any' ? '⏰ Gün içi' : task.time_slot}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {task.completed && task.on_time !== undefined && (
                              <Badge className={task.on_time ? 'bg-green-500' : 'bg-amber-500'}>
                                {task.on_time ? 'Zamanında' : 'Geç'}
                              </Badge>
                            )}
                            {task.completed && task.completed_at && (
                              <span className="text-xs text-muted-foreground">
                                {new Date(task.completed_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {!task.completed && (
                              <Badge variant="secondary">Bekliyor</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                      <p>Bu park için tanımlı görev bulunmuyor</p>
                    </div>
                  )}
                </div>
              </>
            )}
      </DraggableModal>
    </TabsContent>
  );
}

// Vekalet Geçmişi Komponenti - Park Görevlisi Panelinde
const SubstitutionHistory = ({ personnelId }) => {
  const [substitutionHistory, setSubstitutionHistory] = useState({ as_substitute: [], given: [] });
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (personnelId) {
      fetchSubstitutionHistory();
    }
  }, [personnelId]);

  const fetchSubstitutionHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/substitutions/history/${personnelId}`);
      setSubstitutionHistory(response.data);
    } catch (error) {
      console.error("Vekalet geçmişi yüklenirken hata:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active': return <Badge className="bg-green-500 text-white">Aktif</Badge>;
      case 'transferred': return <Badge className="bg-orange-500 text-white">Devredildi</Badge>;
      case 'cancelled': return <Badge className="bg-red-500 text-white">İptal Edildi</Badge>;
      case 'expired': return <Badge className="bg-gray-500 text-white">Tamamlandı</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Vekalet geçmişi yükleniyor...</span>
      </div>
    );
  }

  const hasAsSubstitute = substitutionHistory.as_substitute?.length > 0;
  const hasGiven = substitutionHistory.given?.length > 0;

  return (
    <div className="space-y-6">
      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-300">
          <CardContent className="p-6 text-center">
            <Users className="h-10 w-10 mx-auto mb-2 text-indigo-600" />
            <p className="text-3xl font-bold text-indigo-700">{substitutionHistory.as_substitute?.length || 0}</p>
            <p className="text-sm text-indigo-600 font-medium">Vekil Olarak Baktığım</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300">
          <CardContent className="p-6 text-center">
            <Clock className="h-10 w-10 mx-auto mb-2 text-orange-600" />
            <p className="text-3xl font-bold text-orange-700">{substitutionHistory.given?.length || 0}</p>
            <p className="text-sm text-orange-600 font-medium">Vekalet Verdiğim</p>
          </CardContent>
        </Card>
      </div>

      {/* Vekil Olarak Baktıklarım */}
      <Card>
        <CardHeader className="bg-indigo-50 border-b border-indigo-200">
          <CardTitle className="text-lg text-indigo-800 flex items-center gap-2">
            <Users className="h-5 w-5" />
            Vekil Olarak Baktığım Parklar
          </CardTitle>
          <CardDescription>Başka personelin izinli olduğu dönemlerde baktığım parklar</CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          {!hasAsSubstitute ? (
            <p className="text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
              Henüz vekil olarak bir göreve atanmadınız.
            </p>
          ) : (
            <div className="space-y-3">
              {substitutionHistory.as_substitute.map((sub, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-r from-indigo-50 to-white rounded-lg border border-indigo-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-indigo-800">
                        {sub.original_personnel_name}'ın yerine
                      </p>
                      <p className="text-sm text-gray-600">
                        📅 {formatDate(sub.start_date)} / {formatDate(sub.end_date)}
                      </p>
                    </div>
                    {getStatusBadge(sub.status)}
                  </div>
                  
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Baktığım Parklar:</p>
                    <div className="flex flex-wrap gap-2">
                      {sub.parks?.map(park => (
                        <span key={park.id} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                          🌳 {park.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Transfer bilgisi */}
                  {sub.status === 'transferred' && sub.transferred_to_name && (
                    <div className="mt-2 p-2 bg-orange-50 rounded text-sm text-orange-700">
                      ➡️ {sub.transferred_to_name}'e devredildi
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vekalet Verdiklerim */}
      <Card>
        <CardHeader className="bg-orange-50 border-b border-orange-200">
          <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Vekalet Verdiğim Dönemler
          </CardTitle>
          <CardDescription>İzinli olduğum dönemlerde parklarıma bakan personeller</CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          {!hasGiven ? (
            <p className="text-gray-500 text-center py-6 bg-gray-50 rounded-lg">
              Henüz vekalet verdiğiniz bir dönem bulunmuyor.
            </p>
          ) : (
            <div className="space-y-3">
              {substitutionHistory.given.map((sub, idx) => (
                <div key={idx} className="p-4 bg-gradient-to-r from-orange-50 to-white rounded-lg border border-orange-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-orange-800">
                        {sub.substitute_personnel_name} baktı
                      </p>
                      <p className="text-sm text-gray-600">
                        📅 {formatDate(sub.start_date)} / {formatDate(sub.end_date)}
                      </p>
                    </div>
                    {getStatusBadge(sub.status)}
                  </div>
                  
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Vekalet Verilen Parklar:</p>
                    <div className="flex flex-wrap gap-2">
                      {sub.parks?.map(park => (
                        <span key={park.id} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                          🌳 {park.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
